import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ShoppingBag, Clock, MapPin, ChefHat, Truck, Star, X, Menu as MenuIcon, Phone, Mail, Home, Info, UtensilsCrossed, MessageSquare, Lock, Award, Heart, Users, Search, Plus, Minus, Check, AlertTriangle, RefreshCw, ShieldX, WifiOff, LogIn, UserPlus, Package, User, Download, QrCode } from 'lucide-react';
import QRCode from 'react-qr-code';

const resolveImageUrl = (image) => {
    if (!image) return 'https://via.placeholder.com/300x200?text=No+Image';
    
    // External image links not uploaded to our backend
    if (image.startsWith('http') && !image.includes('/uploads/')) {
        return image;
    }
    
    const idx = image.indexOf('/uploads/');
    if (idx !== -1) {
        const filename = image.substring(idx + 9);
        const apiPath = `/api/uploads/${filename}`;
        
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        
        // If testing on localhost, route to local backend
        if (backendUrl && isLocalhost) {
            return `${backendUrl.replace(/\/$/, '')}${apiPath}`;
        }
        
        // Production: if image is already a full URL like "https://nayanskitchen.com/api/uploads/menu-xxx.jpg"
        if (image.startsWith('http')) {
            if (image.includes('/uploads/') && !image.includes('/api/uploads/')) {
                return image.replace('/uploads/', '/api/uploads/');
            }
            return image;
        }
        
        return apiPath;
    }
    
    return image;
};

// Payment states where money has actually changed hands. An invoice is only a
// valid document once one of these is reached — a Pending/Failed order gets a
// plain order summary instead.
const SETTLED_PAYMENT_STATUSES = ['Paid', 'Refunding', 'Refunded', 'Refund Failed'];
const isInvoiceReady = (order) => SETTLED_PAYMENT_STATUSES.includes(order?.paymentStatus);


const LandingPage = () => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const [menu, setMenu] = useState([]);
    const [cart, setCart] = useState([]);
    const [showCheckout, setShowCheckout] = useState(false);
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const initialCustomerState = isLocalhost ? {
        name: 'blaze', phone: '9876543210', email: 'swapnilbaikar0902@gmail.com', address: 'Mumbai', pincode: '400076', deliveryTimeSlot: '', instructions: '', paymentMethod: '', transactionId: ''
    } : {
        name: '', phone: '', email: '', address: '', pincode: '', deliveryTimeSlot: '', instructions: '', paymentMethod: '', transactionId: ''
    };

    // T+1 delivery windows: next day, 10AM–8PM in 2-hour slots
    const DELIVERY_SLOT_WINDOWS = [
        '10:00 AM - 12:00 PM',
        '12:00 PM - 2:00 PM',
        '2:00 PM - 4:00 PM',
        '4:00 PM - 6:00 PM',
        '6:00 PM - 8:00 PM'
    ];
    const getNextDayDeliverySlots = () => {
        const nextDay = new Date();
        nextDay.setDate(nextDay.getDate() + 1);
        const dateLabel = nextDay.toLocaleDateString('en-IN', {
            weekday: 'short',
            day: 'numeric',
            month: 'short'
        });
        return DELIVERY_SLOT_WINDOWS.map(window => `${dateLabel} · ${window}`);
    };
    const deliveryTimeSlots = getNextDayDeliverySlots();
    const [customer, setCustomer] = useState(initialCustomerState);
    const [loading, setLoading] = useState(true);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [paymentConfig, setPaymentConfig] = useState({ isActive: false });
    const [minOrderValue, setMinOrderValue] = useState(0);
    const [placedOrder, setPlacedOrder] = useState(null);
    const [showTrackOrder, setShowTrackOrder] = useState(false);
    const [trackOrderId, setTrackOrderId] = useState('');
    const [trackResult, setTrackResult] = useState(null);
    const [trackError, setTrackError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submissionError, setSubmissionError] = useState('');
    const [selectedItemDetail, setSelectedItemDetail] = useState(null);
    const [paymentError, setPaymentError] = useState(null); // { title, message, canRetry, provider }
    const navigate = useNavigate();
    const [orderType, setOrderType] = useState('delivery'); // 'delivery' | 'pickup'
    const [currentUser, setCurrentUser] = useState(null);
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [authMode, setAuthMode] = useState('login'); // 'login' | 'register' | 'forgot'
    const [authForm, setAuthForm] = useState({ name: '', email: '', phone: '', password: '', confirmPassword: '' });
    const [authError, setAuthError] = useState('');
    const [authLoading, setAuthLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const [googleAuthState, setGoogleAuthState] = useState('loading'); // 'loading' | 'ready' | 'misconfigured' | 'unavailable'
    const googleTokenClient = useRef(null);
    const [menuRatings, setMenuRatings] = useState({});
    const [showFeedbackModal, setShowFeedbackModal] = useState(false);
    const [feedbackOrder, setFeedbackOrder] = useState(null);
    const [feedbackRatings, setFeedbackRatings] = useState({});
    const [feedbackComment, setFeedbackComment] = useState('');
    const [feedbackName, setFeedbackName] = useState('');
    const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
    const [feedbackDone, setFeedbackDone] = useState(false);

    // Support Messaging
    const [showSupportModal, setShowSupportModal] = useState(false);
    const [supportForm, setSupportForm] = useState({ name: '', email: '', phone: '', subject: 'General Inquiry', message: '' });
    const [supportLoading, setSupportLoading] = useState(false);
    const [supportDone, setSupportDone] = useState(false);

    // Order History
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [customerOrders, setCustomerOrders] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);

    // Profile Management
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [profileForm, setProfileForm] = useState({ name: '', email: '', phone: '', address: '', pincode: '', password: '' });
    const [profileLoading, setProfileLoading] = useState(false);

    // Toast Notifications
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
    };

    // Chatbot States
    const [showChatbot, setShowChatbot] = useState(false);
    const [chatInput, setChatInput] = useState('');
    const [chatMessages, setChatMessages] = useState([
        { role: 'bot', text: "Namaste! 🙏 I'm your Nayan's Kitchen & Delights assistant. How can I help you today?" }
    ]);
    const [isBotTyping, setIsBotTyping] = useState(false);
    const [chatMode, setChatMode] = useState('normal'); // 'normal' | 'collecting_support'

    useEffect(() => {
        fetchMenu();
        fetchPaymentConfig();
        fetchMenuRatings();
        fetchSettings();
        checkAuthState();

        // Handle Cashfree redirect back — {order_id} is filled by Cashfree with the CF order id (e.g. CF_abc123)
        const urlParams = new URLSearchParams(window.location.search);
        if (urlParams.get('cashfree_callback') === 'true') {
            const cfOrderId = urlParams.get('order_id');
            if (cfOrderId) {
                checkCashfreeStatus(cfOrderId);
            }
        }
    }, []);

    const checkCashfreeStatus = async (cfOrderId) => {
        try {
            setShowCheckout(true);
            setLoading(true);

            // Clean URL immediately so refresh doesn't re-trigger
            window.history.replaceState({}, document.title, window.location.pathname);

            const res = await axios.post(`${API_URL}/api/public/cashfree/status`, {
                orderId: cfOrderId  // backend accepts CF_xxx format and resolves to DB id
            });

            if (res.data.success && res.data.status === 'PAID') {
                // Backend has already confirmed & updated the DB order via status check.
                // Use db_order_id returned from backend to display the order.
                setPlacedOrder({ _id: res.data.db_order_id || cfOrderId });
                setCart([]);
                setCustomer(initialCustomerState);
                localStorage.removeItem('pendingCashfreeOrder');
            } else if (res.data.status === 'NOT_FOUND') {
                // Cannot verify — could be expired session OR credentials mismatch
                // NEVER tell customer "no money deducted" — we don't know
                localStorage.removeItem('pendingCashfreeOrder');
                setShowCheckout(true);
                setPaymentError({
                    title: 'Payment Verification Pending',
                    message: 'We could not verify your payment status right now. If money was deducted from your account, please do NOT pay again — contact us and we will process your order or refund manually.',
                    canRetry: false,
                    provider: 'Cashfree'
                });
            } else {
                localStorage.removeItem('pendingCashfreeOrder');
                setShowCheckout(true);
                setPaymentError({
                    title: 'Payment Not Completed',
                    message: res.data.status === 'PENDING' || res.data.status === 'ACTIVE'
                        ? 'Your payment is still being processed. If money was deducted, it will be refunded automatically within 3–5 business days.'
                        : 'Your payment could not be completed. No money has been deducted. Please try again.',
                    canRetry: true,
                    provider: 'Cashfree'
                });
            }
        } catch (err) {
            console.error('Failed checking Cashfree status:', err);
            setShowCheckout(true);
            // Important: do NOT say "no money deducted" here — we don't know
            setPaymentError({
                title: 'Verification Failed',
                message: 'We could not verify your payment status. If money was deducted from your account, please contact us with your Order ID. We will process your order or refund manually.',
                canRetry: false,
                provider: 'Cashfree'
            });
        } finally {
            setLoading(false);
        }
    };

    const checkPhonePeStatus = async (transactionId) => {
        try {
            setShowCheckout(true);
            setLoading(true);

            // Clean URL from callback parameters
            window.history.replaceState({}, document.title, window.location.pathname);

            const res = await axios.post(`${API_URL}/api/public/phonepe/status`, {
                transactionId: transactionId
            });

            if (res.data.success && res.data.code === 'PAYMENT_SUCCESS') {
                // If the check was successful, pull partial order from storage or fallback if empty
                const savedOrderStr = localStorage.getItem('pendingPhonePeOrder');
                if (savedOrderStr) {
                    const savedOrder = JSON.parse(savedOrderStr);
                    // Override properties since we can't reliably load state in direct mount
                    setCart(savedOrder.cart || []);
                    setCustomer(prev => ({
                        ...prev,
                        ...savedOrder.customer,
                        transactionId: transactionId
                    }));
                    await submitOrderData(transactionId, savedOrder);
                    localStorage.removeItem('pendingPhonePeOrder');
                } else {
                    // Fallback using direct state (might be empty if reloading)
                    setCustomer(prev => ({ ...prev, transactionId }));
                    submitOrderData(transactionId);
                }
            } else {
                // Restore cart & customer so user doesn't lose their data
                const savedOrderStr = localStorage.getItem('pendingPhonePeOrder');
                if (savedOrderStr) {
                    try {
                        const savedOrder = JSON.parse(savedOrderStr);
                        setCart(savedOrder.cart || []);
                        setCustomer(prev => ({ ...prev, ...savedOrder.customer }));
                    } catch (_) { }
                    localStorage.removeItem('pendingPhonePeOrder');
                }
                setShowCheckout(true);
                setPaymentError({
                    title: 'Payment Failed',
                    message: res.data.code === 'PAYMENT_PENDING'
                        ? 'Your payment is pending confirmation. If money was deducted, it will be refunded automatically within 3–5 days.'
                        : 'Your payment could not be completed. No money has been deducted. Please try again or choose a different payment method.',
                    canRetry: true,
                    provider: 'PhonePe'
                });
            }
        } catch (err) {
            console.error("Failed checking PhonePe status:", err);
            setShowCheckout(true);
            setPaymentError({
                title: 'Verification Failed',
                message: 'We could not verify your payment status. If money was deducted, please contact support with your Order ID.',
                canRetry: true,
                provider: 'PhonePe'
            });
        } finally {
            setLoading(false);
        }
    };



    const fetchPaymentConfig = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/public/payment-config`);
            setPaymentConfig(res.data || { isActive: false });
        } catch (err) {
            console.error("Failed to load payment config", err);
        }
    };

    const fetchSettings = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/public/settings`);
            setMinOrderValue(res.data.minOrderValue || 0);
        } catch (err) {
            console.error("Failed to load settings", err);
        }
    };

    const fetchMenu = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/api/public/menu`);
            setMenu(res.data);
        } catch (err) {
            console.error("Failed to load menu", err);
        } finally {
            setLoading(false);
        }
    };

    const fetchMenuRatings = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/public/menu-ratings`);
            setMenuRatings(res.data || {});
        } catch { }
    };

    const fetchCustomerOrders = async () => {
        if (!currentUser) return;
        setLoadingHistory(true);
        try {
            const token = localStorage.getItem('customerToken');
            const res = await axios.get(`${API_URL}/api/public/customer-orders`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCustomerOrders(res.data);
        } catch (err) {
            console.error("Failed to fetch order history", err);
        } finally {
            setLoadingHistory(false);
        }
    };

    useEffect(() => {
        if (currentUser) {
            fetchCustomerOrders();
        } else {
            setCustomerOrders([]);
        }
    }, [currentUser]);

    const checkAuthState = () => {
        try {
            const token = localStorage.getItem('customerToken');
            const user = localStorage.getItem('customerUser');
            if (token && user) setCurrentUser(JSON.parse(user));
        } catch { }
    };

    const [forgotSent, setForgotSent] = useState(false);

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        setAuthError('');
        setAuthLoading(true);
        try {
            await axios.post(`${API_URL}/api/public/forgot-password`, { email: authForm.email });
            setForgotSent(true);
        } catch {
            setAuthError('Something went wrong. Please try again.');
        } finally {
            setAuthLoading(false);
        }
    };

    const handleAuthSubmit = async (e) => {
        e.preventDefault();
        setAuthError('');
        setAuthLoading(true);
        try {
            if (authMode === 'register') {
                if (authForm.password !== authForm.confirmPassword) {
                    setAuthError('Passwords do not match');
                    return;
                }
                const res = await axios.post(`${API_URL}/api/public/register`, {
                    name: authForm.name, email: authForm.email, phone: authForm.phone, password: authForm.password
                });
                localStorage.setItem('customerToken', res.data.token);
                localStorage.setItem('customerUser', JSON.stringify(res.data.user));
                setCurrentUser(res.data.user);
                showToast('Account created successfully!');
            } else {
                const res = await axios.post(`${API_URL}/api/public/login`, {
                    email: authForm.email, password: authForm.password
                });
                localStorage.setItem('customerToken', res.data.token);
                localStorage.setItem('customerUser', JSON.stringify(res.data.user));
                setCurrentUser(res.data.user);
                showToast('Welcome back!');
            }
            setShowAuthModal(false);
            setAuthForm({ name: '', email: '', phone: '', password: '', confirmPassword: '' });
        } catch (err) {
            setAuthError(err.response?.data?.message || 'Authentication failed. Please try again.');
        } finally {
            setAuthLoading(false);
        }
    };

    const submitGoogleAuth = async (payload) => {
        setAuthError('');
        try {
            const res = await axios.post(`${API_URL}/api/public/google-login`, payload);
            const { token, user } = res.data;
            localStorage.setItem('customerToken', token);
            localStorage.setItem('customerUser', JSON.stringify(user));
            setCurrentUser(user);
            showToast(`Welcome, ${user.name}!`);
            setShowAuthModal(false);
        } catch (err) {
            console.error('Google Sign-In Error:', err);
            setAuthError(err.response?.data?.message || 'Google Sign-In failed');
        } finally {
            setGoogleLoading(false);
        }
    };

    // Google popup (OAuth token) flow — keeps the button fully ours to style
    useEffect(() => {
        if (!showAuthModal) return;

        const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
        if (!googleClientId || googleClientId.includes('YOUR_GOOGLE_CLIENT_ID')) {
            // CRA compiles .env in at build time — editing it after `npm start`
            // leaves this undefined until the dev server is restarted.
            console.warn('[Google Auth] REACT_APP_GOOGLE_CLIENT_ID is missing from the build. Set it in frontend/.env and RESTART the dev server.');
            setGoogleAuthState('misconfigured');
            return;
        }

        let cancelled = false;
        let waited = 0;
        const initTokenClient = () => {
            if (cancelled) return;

            if (!window.google?.accounts?.oauth2) {
                waited += 200;
                if (waited >= 10000) {
                    // Ten seconds is long past a slow network; something is blocking it.
                    console.error('[Google Auth] accounts.google.com/gsi/client never loaded — check for an ad blocker or network restriction.');
                    setGoogleAuthState('unavailable');
                    return;
                }
                setTimeout(initTokenClient, 200);
                return;
            }

            try {
                googleTokenClient.current = window.google.accounts.oauth2.initTokenClient({
                    client_id: googleClientId,
                    scope: 'openid email profile',
                    callback: (response) => {
                        if (!response?.access_token) {
                            setGoogleLoading(false);
                            setAuthError('Google Sign-In was cancelled');
                            return;
                        }
                        submitGoogleAuth({ accessToken: response.access_token });
                    },
                    error_callback: () => setGoogleLoading(false)
                });
                setGoogleAuthState('ready');
            } catch (e) {
                console.error('[Google Auth] initTokenClient failed:', e);
                setGoogleAuthState('unavailable');
            }
        };
        initTokenClient();

        return () => { cancelled = true; };
    }, [showAuthModal]);

    const handleGoogleSignIn = () => {
        setAuthError('');
        if (!googleTokenClient.current) {
            setAuthError(
                googleAuthState === 'misconfigured'
                    ? 'Google Sign-In is not configured on this site. Please use email and password.'
                    : googleAuthState === 'unavailable'
                        ? "Google Sign-In could not load — it may be blocked by an ad blocker or your network. Please use email and password."
                        : 'Google Sign-In is still loading, please try again in a moment.'
            );
            return;
        }
        setGoogleLoading(true);
        googleTokenClient.current.requestAccessToken();
    };

    const handleLogout = () => {
        localStorage.removeItem('customerToken');
        localStorage.removeItem('customerUser');
        setCurrentUser(null);
        setCustomerOrders([]);
        showToast('Logged out successfully');
    };

    const handleProfileUpdate = async (e) => {
        e.preventDefault();
        setProfileLoading(true);
        try {
            const token = localStorage.getItem('customerToken');
            const res = await axios.put(`${API_URL}/api/public/update-profile`, profileForm, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.data.token) {
                localStorage.setItem('customerToken', res.data.token);
            }
            setCurrentUser(res.data.user);
            localStorage.setItem('customerUser', JSON.stringify(res.data.user));
            showToast('Profile updated successfully!');
            setShowProfileModal(false);
        } catch (err) {
            showToast(err.response?.data?.message || 'Update failed', 'error');
        } finally {
            setProfileLoading(false);
        }
    };

    const handleSupportSubmit = async (e) => {
        e.preventDefault();
        setSupportLoading(true);
        try {
            await axios.post(`${API_URL}/api/public/support`, supportForm);
            setSupportDone(true);
            showToast('Message sent successfully!');
            setSupportForm({ name: '', email: '', phone: '', subject: 'General Inquiry', message: '' });
            setTimeout(() => { setShowSupportModal(false); setSupportDone(false); }, 3000);
        } catch (err) {
            alert("Failed to send message. Please try again.");
        } finally {
            setSupportLoading(false);
        }
    };

    const handleFeedbackSubmit = async (e) => {
        e.preventDefault();
        setFeedbackSubmitting(true);
        try {
            const ratings = Object.entries(feedbackRatings).map(([itemId, rating]) => ({ itemId, rating }));
            await axios.post(`${API_URL}/api/public/feedback`, {
                orderId: feedbackOrder?._id,
                ratings,
                comment: feedbackComment,
                customerName: currentUser ? currentUser.name : (feedbackName || 'Anonymous')
            });
            setFeedbackDone(true);
            showToast('Thank you for your feedback!');
            fetchMenuRatings();
        } catch (err) {
            console.error('Feedback error:', err);
        } finally {
            setFeedbackSubmitting(false);
        }
    };

    // Pre-fill checkout form when logged-in user opens checkout
    useEffect(() => {
        if (currentUser) {
            if (showCheckout) {
                setCustomer(prev => ({
                    ...prev,
                    name: prev.name || currentUser.name || '',
                    phone: prev.phone || currentUser.phone || '',
                    email: prev.email || currentUser.email || '',
                    address: prev.address || currentUser.address || '',
                    pincode: prev.pincode || currentUser.pincode || '',
                }));
            }
            // Pre-fill profile form
            setProfileForm({
                name: currentUser.name || '',
                email: currentUser.email || '',
                phone: currentUser.phone || '',
                address: currentUser.address || '',
                pincode: currentUser.pincode || '',
                password: ''
            });
        }
    }, [showCheckout, currentUser, showProfileModal]);

    // Set default payment method once config loads
    useEffect(() => {
        if (paymentConfig.isActive && paymentConfig.provider && !customer.paymentMethod) {
            setCustomer(prev => ({ ...prev, paymentMethod: paymentConfig.provider }));
        }
    }, [paymentConfig.isActive, paymentConfig.provider]);

    const addToCart = (item) => {
        const existing = cart.find(c => c._id === item._id);
        if (existing) {
            setCart(cart.map(c => c._id === item._id ? { ...c, qty: c.qty + 1 } : c));
        } else {
            setCart([...cart, { ...item, qty: 1 }]);
        }
    };

    const removeFromCart = (id) => {
        setCart(cart.filter(item => item._id !== id));
    };

    const updateQty = (id, delta) => {
        setCart(cart.map(item => {
            if (item._id === id) {
                return { ...item, qty: item.qty + delta };
            }
            return item;
        }).filter(item => item.qty > 0));
    };

    const handleChatSubmit = async (e) => {
        e.preventDefault();
        if (!chatInput.trim()) return;

        const userMsg = chatInput.trim();
        setChatMessages(prev => [...prev, { role: 'user', text: userMsg }]);
        setChatInput('');
        setIsBotTyping(true);

        // Handle Support Collection Mode
        if (chatMode === 'collecting_support') {
            setTimeout(async () => {
                try {
                    await axios.post(`${API_URL}/api/public/support`, {
                        name: currentUser?.name || 'Chat User',
                        email: currentUser?.email || 'chat@user.com',
                        phone: currentUser?.phone || '',
                        subject: 'Message via Chatbot',
                        message: userMsg
                    });
                    setChatMessages(prev => [...prev, { role: 'bot', text: "Got it! 📝 I've forwarded your message to our kitchen team. They'll get back to you soon!" }]);
                    setChatMode('normal');
                } catch (err) {
                    setChatMessages(prev => [...prev, { role: 'bot', text: "Oops, I had trouble sending that. Can you try again later?" }]);
                }
                setIsBotTyping(false);
            }, 1000);
            return;
        }

        // Simple ID detection (24 hex characters for MongoDB)
        const orderIdMatch = userMsg.match(/[0-9a-fA-F]{24}/);

        setTimeout(async () => {
            let response = "";
            const msg = userMsg.toLowerCase();

            if (orderIdMatch) {
                try {
                    const res = await axios.get(`${API_URL}/api/public/order-status/${orderIdMatch[0]}`);
                    response = `I found your order! 📦 Status: ${res.data.status}. (Order Date: ${new Date(res.data.orderDate).toLocaleDateString()})`;
                } catch (err) {
                    response = "I couldn't find an order with that ID. Please double-check it and try again!";
                }
            }
            else if (msg.includes('status') || msg.includes('track') || msg.includes('order')) {
                response = "To track your order, please paste your 24-character Order ID here. You can find it in your 'My Orders' history!";
            }
            else if (msg.includes('support') || msg.includes('human') || msg.includes('person') || msg.includes('help') || msg.includes('contact')) {
                response = "I can definitely help you with that! Please type your message here, and I'll send it directly to our support team. ✍️";
                setChatMode('collecting_support');
            }
            else if (msg.includes('hello') || msg.includes('hi')) response = "Namaste! How can Nayan's Kitchen & Delights help you today?";
            else if (msg.includes('time') || msg.includes('delivery')) response = "We typically deliver within 45-60 minutes depending on your location!";
            else if (msg.includes('menu') || msg.includes('food')) response = "You can scroll down to see our delicious Maharashtrian menu. We have Puran Poli, Misal Pav, and much more!";
            else if (msg.includes('veg')) response = "Yes! We have a wide variety of 100% vegetarian authentic dishes.";
            else if (msg.includes('price')) response = "Our dishes are very affordable, starting from just ₹99!";
            else if (msg.includes('location') || msg.includes('where')) response = "We are located in the heart of Mumbai, serving fresh meals across the city.";
            else response = "I'm still learning! 🎓 Would you like me to send a message to our human support team? Just say 'contact support'.";

            setChatMessages(prev => [...prev, { role: 'bot', text: response }]);
            setIsBotTyping(false);
        }, 800);
    };

    const subtotal = cart.reduce((sum, item) => sum + item.price * item.qty, 0);
    const deliveryFeeAmount = orderType === 'delivery' ? 99 : 0;
    const total = subtotal + deliveryFeeAmount;
    const minQtyViolations = cart.filter(item => item.qty < (item.minQty || 1));
    const minOrderViolation = minOrderValue > 0 && subtotal < minOrderValue;

    const handleOrder = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        if (customer.paymentMethod === 'Paytm' && paymentConfig.provider === 'Paytm') {
            await initiatePaytmPayment();
        } else if (customer.paymentMethod === 'Razorpay' && paymentConfig.provider === 'Razorpay') {
            await initiateRazorpayPayment();
        } else if (customer.paymentMethod === 'PhonePe' && paymentConfig.provider === 'PhonePe') {
            await initiatePhonePePayment();
        } else if (customer.paymentMethod === 'Cashfree' && paymentConfig.provider === 'Cashfree') {
            await initiateCashfreePayment();
        } else {
            submitOrderData();
        }
    };

    const loadRazorpayScript = () => {
        return new Promise((resolve) => {
            if (window.Razorpay) {
                resolve(true);
            } else {
                const script = document.createElement("script");
                script.src = "https://checkout.razorpay.com/v1/checkout.js";
                script.onload = () => resolve(true);
                script.onerror = () => resolve(false);
                document.body.appendChild(script);
            }
        });
    };

    const initiateRazorpayPayment = async () => {
        try {
            setLoading(true);
            const initRes = await axios.post(`${API_URL}/api/public/razorpay/init`, {
                amount: total
            });

            const { id, currency, amount, key_id } = initRes.data;
            if (!id) {
                setPaymentError({ title: 'Payment Init Failed', message: 'Could not start Razorpay payment. Please try again.', canRetry: true, provider: 'Razorpay' });
                setLoading(false);
                setIsSubmitting(false);
                return;
            }

            const loaded = await loadRazorpayScript();
            if (!loaded) {
                setPaymentError({ title: 'SDK Load Failed', message: 'Razorpay payment SDK could not be loaded. Please check your internet connection and try again.', canRetry: true, provider: 'Razorpay' });
                setLoading(false);
                setIsSubmitting(false);
                return;
            }

            const options = {
                key: key_id,
                amount: amount.toString(),
                currency: currency,
                name: "Nayan's Kitchen & Delights",
                description: "Order Payment",
                order_id: id,
                handler: function (response) {
                    setCustomer(prev => ({ ...prev, transactionId: response.razorpay_payment_id }));
                    submitOrderData(response.razorpay_payment_id);
                },
                prefill: {
                    name: customer.name,
                    email: customer.email,
                    contact: customer.phone
                },
                theme: {
                    color: "#d97706"
                }
            };

            const paymentObject = new window.Razorpay(options);
            paymentObject.open();

            setLoading(false);
        } catch (err) {
            console.error(err);
            setPaymentError({ title: 'Payment Failed', message: 'Razorpay initialization failed: ' + err.message, canRetry: true, provider: 'Razorpay' });
            setLoading(false);
            setIsSubmitting(false);
        }
    };

    const loadPaytmScript = (mid) => {
        return new Promise((resolve) => {
            if (window.Paytm && window.Paytm.CheckoutJS) {
                resolve(true);
            } else {
                const script = document.createElement("script");
                script.src = `https://securegw-stage.paytm.in/merchantpgpui/checkoutjs/merchants/${mid}.js`;
                script.crossOrigin = "anonymous";
                script.onload = () => resolve(true);
                script.onerror = () => resolve(false);
                document.body.appendChild(script);
            }
        });
    };

    const initiatePaytmPayment = async () => {
        try {
            setLoading(true);
            const initRes = await axios.post(`${API_URL}/api/public/paytm/init`, {
                amount: total,
                customerPhone: customer.phone || '0000000000'
            });

            const { body, mid, orderId } = initRes.data;
            if (!body || !body.txnToken) {
                setPaymentError({ title: 'Payment Init Failed', message: 'Could not start Paytm payment. Please try again.', canRetry: true, provider: 'Paytm' });
                setLoading(false);
                setIsSubmitting(false);
                return;
            }

            const txnToken = body.txnToken;
            const loaded = await loadPaytmScript(mid);
            if (!loaded) {
                setPaymentError({ title: 'SDK Load Failed', message: 'Paytm payment SDK could not be loaded. Please check your internet connection.', canRetry: true, provider: 'Paytm' });
                setLoading(false);
                setIsSubmitting(false);
                return;
            }

            const config = {
                "root": "",
                "flow": "DEFAULT",
                "data": {
                    "orderId": orderId,
                    "token": txnToken,
                    "tokenType": "TXN_TOKEN",
                    "amount": total
                },
                "handler": {
                    "notifyMerchant": function (eventName, data) {
                        console.log("notifyMerchant handler", eventName, data);
                    },
                    "transactionStatus": function (paymentStatus) {
                        console.log("paymentStatus", paymentStatus);
                        if (paymentStatus.STATUS === "TXN_SUCCESS") {
                            setCustomer(prev => ({ ...prev, transactionId: paymentStatus.TXNID }));
                            submitOrderData(paymentStatus.TXNID);
                        } else {
                            setPaymentError({ title: 'Payment Failed', message: 'Payment was not successful: ' + (paymentStatus.RESPMSG || paymentStatus.STATUS) + '. No money has been deducted.', canRetry: true, provider: 'Paytm' });
                            setIsSubmitting(false);
                        }
                        window.Paytm.CheckoutJS.close();
                    }
                }
            };

            if (window.Paytm && window.Paytm.CheckoutJS) {
                window.Paytm.CheckoutJS.init(config).then(function onSuccess() {
                    window.Paytm.CheckoutJS.invoke();
                }).catch(function onError(error) {
                    console.error("Paytm Init Error => ", error);
                });
            }
            setLoading(false);
        } catch (err) {
            console.error("Paytm Init Error:", err);
            setPaymentError({ title: 'Payment Failed', message: 'Paytm initialization failed: ' + err.message, canRetry: true, provider: 'Paytm' });
            setLoading(false);
            setIsSubmitting(false);
        }
    };

    const loadPhonePeScript = () => {
        return new Promise((resolve) => {
            if (window.PhonePeCheckout) {
                resolve(true);
            } else {
                const script = document.createElement("script");
                script.src = "https://mercury.phonepe.com/web/bundle/checkout.js";
                script.onload = () => resolve(true);
                script.onerror = () => resolve(false);
                document.body.appendChild(script);
            }
        });
    };

    const initiatePhonePePayment = async () => {
        try {
            setLoading(true);
            const transactionId = 'ORD_' + Date.now();

            // Save state to recovery after potential redirect/reload
            localStorage.setItem('pendingPhonePeOrder', JSON.stringify({
                customer: customer,
                cart: cart,
                total: total,
                orderType: orderType
            }));

            const initRes = await axios.post(`${API_URL}/api/public/phonepe/init`, {
                amount: total,
                customerPhone: customer.phone || '0000000000',
                orderId: transactionId
            });

            if (initRes.data && initRes.data.success && initRes.data.redirectUrl) {
                const loaded = await loadPhonePeScript();
                if (!loaded) {
                    setPaymentError({ title: 'SDK Load Failed', message: 'PhonePe payment SDK could not be loaded. Please check your internet connection.', canRetry: true, provider: 'PhonePe' });
                    setLoading(false);
                    setIsSubmitting(false);
                    return;
                }

                window.PhonePeCheckout.transact({
                    tokenUrl: initRes.data.redirectUrl,
                    type: "IFRAME",
                    callback: (response) => {
                        if (response === 'USER_CANCEL') {
                            setPaymentError({ title: 'Payment Cancelled', message: 'You cancelled the payment. Your cart has been saved — you can try again anytime.', canRetry: true, provider: 'PhonePe' });
                            setLoading(false);
                            setIsSubmitting(false);
                        } else if (response === 'CONCLUDED') {
                            checkPhonePeStatus(transactionId);
                        } else {
                            checkPhonePeStatus(transactionId);
                        }
                    }
                });
            } else {
                setPaymentError({ title: 'Payment Init Failed', message: 'Could not start PhonePe payment. Please try again.', canRetry: true, provider: 'PhonePe' });
                setLoading(false);
                setIsSubmitting(false);
            }
        } catch (err) {
            console.error("PhonePe Init Error:", err);
            setPaymentError({ title: 'Payment Failed', message: 'PhonePe initialization failed: ' + (err.response?.data?.message || err.message), canRetry: true, provider: 'PhonePe' });
            setLoading(false);
            setIsSubmitting(false);
        }
    };

    const loadCashfreeSDK = () => {
        return new Promise((resolve) => {
            if (window.Cashfree) {
                resolve(true);
            } else {
                const script = document.createElement("script");
                script.src = "https://sdk.cashfree.com/js/v3/cashfree.js";
                script.onload = () => resolve(true);
                script.onerror = () => resolve(false);
                document.body.appendChild(script);
            }
        });
    };

    const initiateCashfreePayment = async () => {
        try {
            setLoading(true);

            // Build the full order data to pre-save server-side
            const orderData = {
                customer: {
                    name: customer.name,
                    phone: customer.phone,
                    email: customer.email,
                    address: customer.address,
                    pincode: customer.pincode
                },
                items: cart.map(c => ({
                    menuItem: c._id,
                    name: c.name,
                    price: c.price,
                    quantity: c.qty
                })),
                specialInstructions: customer.instructions,
                paymentMethod: 'Cashfree',
                deliveryMethod: orderType === 'delivery' ? 'Delivery' : 'Pickup',
                deliveryFee: deliveryFeeAmount,
                deliveryTimeSlot: orderType === 'delivery' ? (customer.deliveryTimeSlot || '') : '',
                total: total
            };

            const initRes = await axios.post(`${API_URL}/api/public/cashfree/init`, {
                amount: total,
                customerId: customer.phone || '0000000000',
                customerName: customer.name || 'Guest',
                customerEmail: customer.email || 'guest@example.com',
                customerPhone: customer.phone || '0000000000',
                orderData: orderData
                // No redirectUrl — the backend builds it from CLIENT_URL env var
                // using the {order_id} placeholder which Cashfree fills authoritatively
            });

            if (initRes.data && initRes.data.success && initRes.data.payment_session_id) {
                const loaded = await loadCashfreeSDK();
                if (!loaded) {
                    setPaymentError({ title: 'SDK Load Failed', message: 'Cashfree payment SDK could not be loaded. Please check your internet connection.', canRetry: true, provider: 'Cashfree' });
                    setLoading(false);
                    setIsSubmitting(false);
                    return;
                }

                const cashfree = window.Cashfree({
                    mode: initRes.data.environment === 'production' ? 'production' : 'sandbox'
                });

                // Redirect to Cashfree — on return, URL will contain the CF order_id
                cashfree.checkout({
                    paymentSessionId: initRes.data.payment_session_id,
                    redirectTarget: '_self'
                });
                // (page will redirect — no further code executes)
            } else {
                setPaymentError({ title: 'Payment Init Failed', message: 'Could not start Cashfree payment. Please try again.', canRetry: true, provider: 'Cashfree' });
                setLoading(false);
                setIsSubmitting(false);
            }
        } catch (err) {
            console.error('Cashfree Init Error:', err);
            setPaymentError({ title: 'Payment Failed', message: 'Cashfree initialization failed: ' + (err.response?.data?.message || err.message), canRetry: true, provider: 'Cashfree' });
            setLoading(false);
            setIsSubmitting(false);
        }
    };

    const submitOrderData = async (txnId = customer.transactionId, preloadedData = null, cashfreeOrderId = null) => {
        const orderCart = preloadedData ? preloadedData.cart : cart;
        const orderCustomer = preloadedData ? preloadedData.customer : customer;
        const orderTotal = preloadedData ? preloadedData.total : total;
        const orderDeliveryType = preloadedData ? (preloadedData.orderType || 'delivery') : orderType;

        const orderData = {
            customer: {
                name: orderCustomer.name,
                phone: orderCustomer.phone,
                email: orderCustomer.email,
                address: orderCustomer.address,
                pincode: orderCustomer.pincode
            },
            paymentMethod: orderCustomer.paymentMethod,
            transactionId: txnId,
            specialInstructions: orderCustomer.instructions,
            deliveryMethod: orderDeliveryType === 'delivery' ? 'Delivery' : 'Pickup',
            deliveryFee: orderDeliveryType === 'delivery' ? 99 : 0,
            deliveryTimeSlot: orderDeliveryType === 'delivery' ? (orderCustomer.deliveryTimeSlot || '') : '',
            items: orderCart.map(c => ({
                menuItem: c._id,
                name: c.name,
                price: c.price,
                quantity: c.qty
            })),
            total: orderTotal
        };
        try {
            setIsSubmitting(true);
            setSubmissionError('');
            const res = await axios.post(`${API_URL}/api/public/order`, orderData);
            setPlacedOrder(res.data.order);
            showToast('Order placed successfully!');
            setFeedbackOrder(res.data.order);
            setFeedbackRatings({});
            setFeedbackComment('');
            setFeedbackName(currentUser ? currentUser.name : '');
            setFeedbackDone(false);
            // Show feedback prompt after 2 seconds on success screen
            setTimeout(() => setShowFeedbackModal(true), 2000);
            setCart([]);
            setCustomer(initialCustomerState);
        } catch (err) {
            // ─── CRITICAL: Payment was confirmed but order save failed ───
            // If this was a Cashfree payment that was already PAID, auto-initiate refund
            if (cashfreeOrderId && orderCustomer.paymentMethod === 'Cashfree') {
                console.warn('Order save failed after confirmed Cashfree payment. Initiating auto-refund for:', cashfreeOrderId);
                try {
                    await axios.post(`${API_URL}/api/public/cashfree/refund`, {
                        orderId: cashfreeOrderId,
                        amount: orderTotal,
                        reason: 'Order placement failed after payment confirmation'
                    });
                    setPaymentError({
                        title: 'Order Failed — Refund Initiated',
                        message: `Your payment of ₹${orderTotal.toFixed(2)} was successful but we couldn't save your order due to a technical issue. A full refund has been automatically initiated and will reach you within 5–7 business days. Sorry for the inconvenience.`,
                        canRetry: false,
                        provider: 'Cashfree'
                    });
                } catch (refundErr) {
                    console.error('Auto-refund also failed:', refundErr);
                    setPaymentError({
                        title: 'Order Failed — Contact Support',
                        message: `Your payment was successful but we couldn't save your order, and the automatic refund also encountered an error. Please contact us immediately with your Order ID: ${cashfreeOrderId}. We will manually process your refund.`,
                        canRetry: false,
                        provider: 'Cashfree'
                    });
                }
            } else {
                setSubmissionError(err.response?.data?.message || "Order placement failed. Please try again.");
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const cartCount = cart.reduce((sum, item) => sum + item.qty, 0);

    const scrollToSection = (id) => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
        setMobileMenuOpen(false);
    };

    const handleTrackOrder = async (e) => {
        e.preventDefault();
        setTrackError('');
        setTrackResult(null);
        try {
            const cleanId = trackOrderId.replace(/#/g, '').trim();
            const res = await axios.get(`${API_URL}/api/public/order-status/${cleanId}`);
            setTrackResult(res.data);
        } catch (err) {
            setTrackError(err.response?.data?.message || 'Failed to track order using this ID');
        }
    };

    return (
        <div className="min-h-screen bg-white">
            {/* Navigation Bar */}
            <nav className="bg-white/95 backdrop-blur-md shadow-md sticky top-0 z-50 border-b border-amber-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-20">
                        {/* Brand */}
                        <div className="flex items-center gap-3 cursor-pointer group" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                            <div className="relative">
                                <img
                                    src="/logo.png"
                                    alt="Nayan's Kitchen Logo"
                                    className="w-10 h-10 sm:w-14 sm:h-14 object-contain rounded-2xl shadow-lg border-2 border-amber-100 group-hover:scale-110 transition-transform duration-300"
                                />
                                <div className="absolute -bottom-1 -right-1 bg-green-500 w-4 h-4 rounded-full border-2 border-white"></div>
                            </div>
                            <div className="flex flex-col">
                                <h1 className="text-base sm:text-2xl font-black text-gray-900 leading-none uppercase tracking-tighter">Nayan's Kitchen & Delights</h1>
                                <span className="hidden sm:block text-[10px] font-bold text-amber-600 uppercase tracking-widest mt-1.5 animate-pulse">Taste That Touches the Soul</span>
                            </div>
                        </div>

                        {/* Desktop Navigation */}
                        <div className="hidden md:flex items-center gap-6">
                            <button onClick={() => scrollToSection('home')} className="flex items-center gap-2 text-gray-700 hover:text-amber-700 font-semibold transition-colors">
                                <Home size={18} />
                                Home
                            </button>
                            <button onClick={() => scrollToSection('menu')} className="flex items-center gap-2 text-gray-700 hover:text-amber-700 font-semibold transition-colors">
                                <UtensilsCrossed size={18} />
                                Menu
                            </button>
                            <button onClick={() => setShowTrackOrder(true)} className="flex items-center gap-2 text-gray-700 hover:text-amber-700 font-semibold transition-colors">
                                <Search size={18} />
                                Track
                            </button>

                            {currentUser ? (
                                <div className="flex items-center gap-4 border-l pl-6 border-amber-100">
                                    <button
                                        onClick={() => setShowHistoryModal(true)}
                                        className="flex items-center gap-2 text-gray-700 hover:text-amber-700 font-bold transition-colors"
                                    >
                                        <Package size={18} />
                                        Orders
                                    </button>
                                    <button
                                        onClick={() => setShowProfileModal(true)}
                                        className="flex items-center gap-2 text-gray-700 hover:text-amber-700 font-bold transition-colors"
                                    >
                                        <User size={18} />
                                        Profile
                                    </button>
                                    <div className="flex flex-col items-end">
                                        <span className="text-sm font-bold text-gray-900">{currentUser.name}</span>
                                        <button onClick={handleLogout} className="text-xs font-bold text-amber-700 hover:text-amber-800 flex items-center gap-1">
                                            Logout <LogIn size={12} />
                                        </button>
                                    </div>
                                    <div className="bg-amber-100 p-2 rounded-full text-amber-700">
                                        <User size={20} />
                                    </div>
                                </div>
                            ) : (
                                <div className="flex items-center gap-3 border-l pl-6 border-amber-100">
                                    <button
                                        onClick={() => { setAuthMode('login'); setShowAuthModal(true); }}
                                        className="text-gray-700 hover:text-amber-700 font-bold transition-colors"
                                    >
                                        Login
                                    </button>
                                    <button
                                        onClick={() => { setAuthMode('register'); setShowAuthModal(true); }}
                                        className="bg-amber-600 text-white px-5 py-2.5 rounded-xl font-bold hover:bg-amber-700 transition-all shadow-md active:scale-95"
                                    >
                                        Sign Up
                                    </button>
                                </div>
                            )}
                            {!currentUser && (
                                <button
                                    onClick={() => navigate('/admin-login')}
                                    className="flex items-center gap-2 border-l pl-6 border-amber-100 text-gray-500 hover:text-amber-700 font-semibold transition-colors text-sm"
                                    title="Admin Login"
                                >
                                    <Lock size={16} /> Admin
                                </button>
                            )}
                        </div>

                        {/* Mobile Menu Button */}
                        <button
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            className="md:hidden p-2 rounded-lg hover:bg-amber-50"
                        >
                            <MenuIcon size={24} className="text-gray-700" />
                        </button>
                    </div>

                    {/* Mobile Navigation */}
                    {mobileMenuOpen && (
                        <div className="md:hidden py-4 border-t border-amber-100 animate-slideUp">
                            <div className="flex flex-col gap-3">
                                <button onClick={() => scrollToSection('home')} className="flex items-center gap-2 text-gray-700 hover:text-amber-700 font-semibold py-2">
                                    <Home size={18} /> Home
                                </button>
                                <button onClick={() => scrollToSection('about')} className="flex items-center gap-2 text-gray-700 hover:text-amber-700 font-semibold py-2">
                                    <Info size={18} /> About
                                </button>
                                <button onClick={() => scrollToSection('menu')} className="flex items-center gap-2 text-gray-700 hover:text-amber-700 font-semibold py-2">
                                    <UtensilsCrossed size={18} /> Menu
                                </button>
                                <button onClick={() => scrollToSection('contact')} className="flex items-center gap-2 text-gray-700 hover:text-amber-700 font-semibold py-2">
                                    <MessageSquare size={18} /> Contact
                                </button>
                                <button onClick={() => { setShowTrackOrder(true); setMobileMenuOpen(false); }} className="flex items-center gap-2 text-gray-700 hover:text-amber-700 font-semibold py-2">
                                    <Search size={18} /> Track Order
                                </button>
                                {currentUser ? (
                                    <div className="pt-2 border-t border-amber-100">
                                        <p className="text-sm text-gray-500 mb-2">Logged in as <strong>{currentUser.name}</strong></p>
                                        <button
                                            onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                                            className="w-full flex items-center gap-2 text-red-600 hover:text-red-700 font-semibold py-2"
                                        >
                                            Logout
                                        </button>
                                    </div>
                                ) : (
                                    <div className="pt-2 border-t border-amber-100 flex flex-col gap-2">
                                        <button
                                            onClick={() => { setAuthMode('login'); setShowAuthModal(true); setMobileMenuOpen(false); }}
                                            className="w-full py-2.5 text-amber-700 font-bold border-2 border-amber-600 rounded-xl hover:bg-amber-50 transition-all"
                                        >
                                            Login
                                        </button>
                                        <button
                                            onClick={() => { setAuthMode('register'); setShowAuthModal(true); setMobileMenuOpen(false); }}
                                            className="w-full py-2.5 bg-amber-600 text-white font-bold rounded-xl hover:bg-amber-700 transition-all"
                                        >
                                            Sign Up
                                        </button>
                                        <button
                                            onClick={() => { navigate('/admin-login'); setMobileMenuOpen(false); }}
                                            className="flex items-center gap-2 justify-center text-gray-500 hover:text-amber-700 font-semibold py-1 text-sm"
                                        >
                                            <Lock size={15} /> Admin Login
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </nav>

            {/* Hero Section with Restaurant Photo */}
            <section id="home" className="relative h-[600px] overflow-hidden">
                {/* Background Image */}
                <div className="absolute inset-0">
                    <img
                        src="https://sp-ao.shortpixel.ai/client/to_auto,q_lossless,ret_img,w_851/https://www.orientbell.com/blog/wp-content/uploads/2024/03/850x450-Pix_4-3.jpg"
                        alt="Restaurant Interior"
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
                </div>

                {/* Content */}
                <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
                    <div className="max-w-2xl text-white">
                        <div className="inline-flex items-center gap-2 bg-amber-600/90 backdrop-blur-sm px-4 py-2 rounded-full mb-6 animate-slideUp">
                            <Award size={20} />
                            <span className="font-semibold">Authentic Maharashtrian Flavors</span>
                        </div>
                        <h1 className="text-3xl sm:text-5xl md:text-7xl font-bold mb-6 animate-slideUp leading-tight">
                            Welcome to<br />Nayan's Kitchen & Delights
                        </h1>
                        <p className="text-xl md:text-2xl text-gray-200 mb-8 animate-slideUp leading-relaxed">
                            Experience traditional Maharashtrian cuisine crafted with love and authentic recipes passed down through generations
                        </p>
                        <div className="flex flex-wrap gap-4 animate-slideUp">
                            <button
                                onClick={() => scrollToSection('menu')}
                                className="bg-gradient-to-r from-amber-600 to-amber-700 text-white px-10 py-4 rounded-full font-bold text-lg shadow-2xl hover:shadow-3xl transform hover:scale-110 transition-all duration-200"
                            >
                                Explore Menu
                            </button>
                            <button
                                onClick={() => scrollToSection('about')}
                                className="bg-white/20 backdrop-blur-sm border-2 border-white text-white px-10 py-4 rounded-full font-bold text-lg hover:bg-white/30 transition-all duration-200"
                            >
                                Learn More
                            </button>
                        </div>

                        <div className="flex flex-wrap gap-6 mt-12">
                            <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm px-5 py-3 rounded-full border border-white/20">
                                <Clock size={22} />
                                <span className="font-semibold">Next-Day Delivery</span>
                            </div>
                            <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm px-5 py-3 rounded-full border border-white/20">
                                <Heart size={22} />
                                <span className="font-semibold">Made with Love</span>
                            </div>
                            <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm px-5 py-3 rounded-full border border-white/20">
                                <Truck size={22} />
                                <span className="font-semibold">Free Delivery</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* About Section */}
            <section id="about" className="py-20 bg-gradient-to-br from-amber-50 via-white to-orange-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-2 rounded-full mb-4 font-semibold">
                            <Star size={18} />
                            Our Story
                        </div>
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">About Nayan's Kitchen & Delights</h2>
                        <p className="text-gray-600 text-lg max-w-3xl mx-auto leading-relaxed">
                            Every dish we make carries the soul of our Konkan roots — recipes whispered from Grandma's kitchen, flavours shaped by the coastal breeze of Maharashtra, and love that no restaurant can replicate. This is not just food. It's our family's story, served to yours.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-amber-100 text-center hover:shadow-2xl hover:border-amber-300 transition-all duration-300 group">
                            <div className="bg-gradient-to-br from-amber-500 to-amber-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform shadow-lg">
                                <ChefHat className="text-white" size={36} />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-3">Grandma's Recipes</h3>
                            <p className="text-gray-600 leading-relaxed">Recipes passed down from Grandma's kitchen — untouched, unaltered, and made exactly the way they've always been made in our Konkan home.</p>
                        </div>

                        <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-amber-100 text-center hover:shadow-2xl hover:border-amber-300 transition-all duration-300 group">
                            <div className="bg-gradient-to-br from-green-500 to-green-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform shadow-lg">
                                <Heart className="text-white" size={36} />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-3">Made with Love</h3>
                            <p className="text-gray-600 leading-relaxed">Every dish is crafted with care and passion, ensuring you get the homemade taste and warmth in every bite.</p>
                        </div>

                        <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-amber-100 text-center hover:shadow-2xl hover:border-amber-300 transition-all duration-300 group">
                            <div className="bg-gradient-to-br from-blue-500 to-blue-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform shadow-lg">
                                <Truck className="text-white" size={36} />
                            </div>
                            <h3 className="text-2xl font-bold text-gray-900 mb-3">Fresh & Fast</h3>
                            <p className="text-gray-600 leading-relaxed">Order today, enjoy tomorrow! Our T+1 delivery system ensures maximum freshness and quality in every meal.</p>
                        </div>
                    </div>

                    {/* Cultural Touch Section */}
                    <div className="mt-16 bg-gradient-to-r from-amber-600 to-amber-700 rounded-3xl p-8 md:p-12 text-white shadow-2xl">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                            <div>
                                <h3 className="text-3xl md:text-4xl font-bold mb-4">Rooted in Konkan, Made with Heart</h3>
                                <p className="text-amber-100 text-lg leading-relaxed mb-6">
                                    Our flavours come from the coastal kitchens of the Konkan region — where the smell of coconut, tamarind, and kokum fill the air. Grandma's hands shaped these recipes, and now we carry that tradition forward so every bite feels like home.
                                </p>
                                <div className="flex flex-wrap gap-4">
                                    <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
                                        <Award size={20} />
                                        <span className="font-semibold">Authentic Konkan Flavours</span>
                                    </div>
                                    <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
                                        <Users size={20} />
                                        <span className="font-semibold">Grandma's Recipes</span>
                                    </div>
                                    <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
                                        <Heart size={20} />
                                        <span className="font-semibold">Coastal Family Cooking</span>
                                    </div>
                                </div>
                            </div>
                            <div className="relative">
                                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border-2 border-white/30">
                                    <h4 className="text-2xl font-bold mb-4">Signature Dishes</h4>
                                    <ul className="space-y-3">
                                        <li className="flex items-center gap-3">
                                            <div className="w-2 h-2 bg-amber-300 rounded-full"></div>
                                            <span className="text-lg">Puran Poli - Sweet flatbread with lentil filling</span>
                                        </li>
                                        <li className="flex items-center gap-3">
                                            <div className="w-2 h-2 bg-amber-300 rounded-full"></div>
                                            <span className="text-lg">Misal Pav - Spicy sprouts curry</span>
                                        </li>
                                        <li className="flex items-center gap-3">
                                            <div className="w-2 h-2 bg-amber-300 rounded-full"></div>
                                            <span className="text-lg">Vada Pav - Mumbai's favorite street food</span>
                                        </li>
                                        <li className="flex items-center gap-3">
                                            <div className="w-2 h-2 bg-amber-300 rounded-full"></div>
                                            <span className="text-lg">Modak - Traditional sweet dumplings</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Menu Section */}
            <section id="menu" className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-2 rounded-full mb-4 font-semibold">
                            <UtensilsCrossed size={18} />
                            Our Specialties
                        </div>
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Explore Our Menu</h2>
                        <p className="text-gray-600 text-lg">Authentic Maharashtrian delicacies crafted with traditional recipes</p>
                    </div>

                    {loading ? (
                        <div className="text-center py-20">
                            <div className="inline-block animate-spin rounded-full h-16 w-16 border-4 border-amber-600 border-t-transparent"></div>
                            <p className="text-gray-500 mt-4 text-lg">Loading delicious menu...</p>
                        </div>
                    ) : menu.length === 0 ? (
                        <div className="text-center py-20">
                            <UtensilsCrossed className="mx-auto text-gray-300 mb-4" size={64} />
                            <p className="text-gray-500 text-lg">Menu coming soon!</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {menu.map(item => (
                                <div key={item._id} className="bg-white rounded-2xl shadow-xl overflow-hidden border-2 border-amber-100 hover:border-amber-400 hover:shadow-2xl transition-all duration-300 group flex flex-col h-full relative cursor-pointer" onClick={() => setSelectedItemDetail(item)}>
                                    <div className="relative overflow-hidden h-56 shrink-0">
                                        <img
                                            src={resolveImageUrl(item.image)}
                                            alt={item.name}
                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                                        <div className="absolute top-4 right-4 bg-white px-4 py-2 rounded-full shadow-lg">
                                            <span className="text-amber-700 font-bold text-xl">₹{item.price}</span>
                                        </div>
                                    </div>
                                    <div className="p-6 flex flex-col flex-1">
                                        <div className="flex justify-between items-start mb-2">
                                            <h3 className="text-xl font-bold text-gray-900">
                                                {item.name}
                                                {item.shortDescription && (
                                                    <span className="text-sm font-medium text-amber-600 ml-1">
                                                        ({item.shortDescription})
                                                    </span>
                                                )}
                                            </h3>
                                            <div className="flex flex-col items-end gap-1 shrink-0 ml-2">
                                                <span className="bg-amber-100 text-amber-800 text-[10px] uppercase px-2 py-0.5 rounded-full font-bold">{item.category}</span>
                                                {menuRatings[item._id] && (
                                                    <div className="flex flex-col items-end gap-0.5">
                                                        <div className="flex items-center gap-0.5 text-amber-500">
                                                            {[1, 2, 3, 4, 5].map((star) => (
                                                                <Star
                                                                    key={star}
                                                                    size={10}
                                                                    className={star <= Math.round(menuRatings[item._id].avg) ? "fill-amber-500" : "text-gray-200"}
                                                                />
                                                            ))}
                                                            <span className="text-[10px] font-black ml-1 text-amber-700">{menuRatings[item._id].avg}</span>
                                                        </div>
                                                        <span className="text-[9px] text-gray-400 font-medium">({menuRatings[item._id].count} reviews)</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <p className="text-gray-600 mb-4 line-clamp-2 flex-1">{item.description}</p>
                                        <div className="flex items-center justify-between mt-auto">
                                            {cart.find(c => c._id === item._id) ? (
                                                <div className="flex items-center justify-between w-full bg-amber-50 rounded-xl p-1 border-2 border-amber-200" onClick={e => e.stopPropagation()}>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); updateQty(item._id, -1); }}
                                                        className="w-10 h-10 bg-white text-amber-700 rounded-lg shadow-sm flex items-center justify-center hover:bg-amber-100 transition-colors"
                                                    >
                                                        <Minus size={18} />
                                                    </button>
                                                    <span className="font-bold text-lg text-amber-900">{cart.find(c => c._id === item._id).qty}</span>
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); updateQty(item._id, 1); }}
                                                        className="w-10 h-10 bg-amber-600 text-white rounded-lg shadow-sm flex items-center justify-center hover:bg-amber-700 transition-colors"
                                                    >
                                                        <Plus size={18} />
                                                    </button>
                                                </div>
                                            ) : (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        addToCart(item);
                                                    }}
                                                    className="w-full bg-gradient-to-r from-amber-600 to-amber-700 text-white font-bold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-200 flex items-center justify-center gap-2"
                                                >
                                                    <Plus size={20} />
                                                    Add to Cart
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* QR Code Section */}
            <section className="py-16 bg-gradient-to-br from-amber-900 to-amber-800">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col md:flex-row items-center justify-between gap-10">
                        <div className="text-white text-center md:text-left">
                            <div className="inline-flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full mb-4">
                                <QrCode size={18} />
                                <span className="font-semibold text-sm">Scan & Download</span>
                            </div>
                            <h2 className="text-3xl md:text-4xl font-bold mb-3">Get Our Full Menu</h2>
                            <p className="text-amber-200 text-lg mb-6 leading-relaxed">
                                Scan the QR code with your phone camera to view our complete menu with prices, pickup & delivery info — and download the PDF instantly.
                            </p>
                            <div className="flex flex-wrap gap-3 justify-center md:justify-start">
                                <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full text-sm font-semibold">
                                    <Check size={16} /> Full Menu with Prices
                                </div>
                                <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full text-sm font-semibold">
                                    <Download size={16} /> Direct PDF Download
                                </div>
                                <div className="flex items-center gap-2 bg-white/20 px-4 py-2 rounded-full text-sm font-semibold">
                                    <MapPin size={16} /> Address & Contact
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col items-center gap-4 flex-shrink-0">
                            <div className="bg-white p-4 rounded-2xl shadow-2xl">
                                <QRCode
                                    value="https://nayanskitchen.com/menu"
                                    size={180}
                                    fgColor="#5C2D00"
                                    bgColor="#FFFFFF"
                                />
                            </div>
                            <p className="text-amber-200 text-sm font-medium">nayanskitchen.com/menu</p>
                            <a
                                href="/menu"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 bg-white text-amber-900 font-bold px-6 py-3 rounded-full hover:bg-amber-50 transition-all shadow-lg"
                            >
                                <Download size={18} /> Open Menu Page
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* Contact Section */}
            <section id="contact" className="py-20 bg-gradient-to-br from-amber-50 via-white to-orange-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-16">
                        <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-2 rounded-full mb-4 font-semibold">
                            <MessageSquare size={18} />
                            Contact Us
                        </div>
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Get In Touch</h2>
                        <p className="text-gray-600 text-lg">We'd love to hear from you</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
                        <a href="tel:+919975512357" className="bg-white rounded-2xl p-8 shadow-lg border-2 border-amber-100 text-center hover:shadow-xl hover:border-amber-300 transition-all block group">
                            <div className="bg-gradient-to-br from-amber-600 to-amber-700 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg group-hover:scale-110 transition-transform">
                                <Phone className="text-white" size={28} />
                            </div>
                            <h3 className="font-bold text-gray-900 mb-2 text-lg">Phone</h3>
                            <p className="text-gray-600 group-hover:text-amber-700 transition-colors">+91 99755 12357</p>
                        </a>

                        <a href="mailto:hellonayanskitchen@gmail.com" className="bg-white rounded-2xl p-8 shadow-lg border-2 border-amber-100 text-center hover:shadow-xl hover:border-amber-300 transition-all block group">
                            <div className="bg-gradient-to-br from-amber-600 to-amber-700 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg group-hover:scale-110 transition-transform">
                                <Mail className="text-white" size={28} />
                            </div>
                            <h3 className="font-bold text-gray-900 mb-2 text-lg">Email</h3>
                            <p className="text-gray-600 group-hover:text-amber-700 transition-colors">hellonayanskitchen@gmail.com</p>
                        </a>

                        <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-amber-100 text-center hover:shadow-xl hover:border-amber-300 transition-all">
                            <div className="bg-gradient-to-br from-amber-600 to-amber-700 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                                <MapPin className="text-white" size={28} />
                            </div>
                            <h3 className="font-bold text-gray-900 mb-2 text-lg">Location</h3>
                            <p className="text-gray-600 text-sm">Kolkata , West Bengal</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-gradient-to-r from-amber-900 to-amber-800 text-white py-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col md:flex-row justify-between items-center">
                        <div className="flex items-center gap-3 mb-4 md:mb-0">
                            <div className="bg-white/15 p-1.5 rounded-xl">
                                <img src="/logo.png" alt="Nayan's Kitchen Logo" className="w-11 h-11 object-contain rounded-lg" />
                            </div>
                            <div>
                                <h3 className="text-2xl font-bold">Nayan's Kitchen & Delights</h3>
                                <p className="text-amber-200 text-sm font-semibold">Taste That Touches the Soul</p>
                            </div>
                        </div>
                        <div className="flex flex-wrap gap-4 text-amber-200 text-sm mt-4 md:mt-0 justify-center">
                            <a href="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</a>
                            <a href="/terms-conditions" className="hover:text-white transition-colors">Terms & Conditions</a>
                            <a href="/refund-policy" className="hover:text-white transition-colors">Refund & Cancellation</a>
                        </div>
                        <p className="text-amber-200 mt-4 md:mt-0">© 2026 Nayan's Kitchen & Delights. All rights reserved.</p>
                    </div>
                </div>
            </footer>

            {/* Floating Cart Button */}
            {cartCount > 0 && (
                <button
                    onClick={() => setShowCheckout(true)}
                    className="fixed bottom-6 right-6 bg-gradient-to-r from-amber-600 to-amber-700 text-white px-8 py-4 rounded-full shadow-2xl hover:shadow-3xl transform hover:scale-110 transition-all duration-200 flex items-center gap-3 z-50 animate-slideUp"
                >
                    <ShoppingBag size={24} />
                    <span className="font-bold text-lg">Cart ({cartCount})</span>
                    <span className="bg-white text-amber-700 px-3 py-1 rounded-full font-bold">₹{subtotal.toFixed(2)}</span>
                </button>
            )}

            {/* Checkout Modal */}
            {showCheckout && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fadeIn">
                    <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-slideUp">
                        <div className="sticky top-0 bg-gradient-to-r from-amber-600 to-amber-700 text-white p-6 rounded-t-3xl flex justify-between items-center z-10">
                            <h2 className="text-2xl font-bold">{placedOrder ? 'Success!' : 'Your Order'}</h2>
                            <button onClick={() => {
                                setShowCheckout(false);
                                setPlacedOrder(null);
                                setSubmissionError('');
                            }} className="hover:bg-white/20 p-2 rounded-full transition-colors">
                                <X size={24} />
                            </button>
                        </div>

                        <div className="p-6">
                            {placedOrder ? (
                                <div className="text-center py-10 px-4 animate-fadeIn">
                                    <div className="bg-green-100 text-green-600 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm border-2 border-green-200">
                                        <Check size={40} />
                                    </div>
                                    <h3 className="text-3xl font-bold text-gray-900 mb-2">Order Confirmed!</h3>
                                    <p className="text-gray-600 text-lg mb-8">Thank you for dining with Nayan's Kitchen & Delights. Your authentic Maharashtrian meal will be delivered tomorrow.</p>

                                    <div className="bg-amber-50 border-2 border-amber-100 rounded-3xl p-8 mb-8 max-w-sm mx-auto shadow-sm">
                                        <div className="text-sm font-bold text-amber-600 uppercase tracking-widest mb-2">Order ID</div>
                                        <div className="text-2xl font-mono text-gray-900 bg-white py-4 rounded-xl shadow-inner mb-4 select-all">
                                            #{placedOrder._id.toString().slice(-6).toUpperCase()}
                                        </div>
                                        <p className="text-xs text-gray-500 italic">Please save this 6-digit ID to track your order status</p>
                                    </div>

                                    <button
                                        onClick={() => { setShowCheckout(false); setPlacedOrder(null); }}
                                        className="bg-gradient-to-r from-amber-600 to-amber-700 text-white px-10 py-4 rounded-2xl font-bold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all"
                                    >
                                        Back to Home
                                    </button>
                                </div>
                            ) : (
                                <>
                                    {/* Cart Items */}
                                    <div className="space-y-4 mb-6">
                                        {cart.length === 0 ? (
                                            <div className="text-center py-10">
                                                <ShoppingBag className="mx-auto text-gray-300 mb-4" size={48} />
                                                <p className="text-gray-500">Your cart is empty</p>
                                            </div>
                                        ) : (
                                            cart.map(item => {
                                                const min = item.minQty || 1;
                                                const violated = item.qty < min;
                                                return (
                                                <div key={item._id} className={`bg-amber-50 p-4 rounded-xl border-2 transition-colors ${violated ? 'border-red-300' : 'border-amber-100 hover:border-amber-300'}`}>
                                                    <div className="flex items-center gap-4">
                                                        <img src={resolveImageUrl(item.image)} alt={item.name} className="w-20 h-20 rounded-lg object-cover" />
                                                        <div className="flex-1">
                                                            <h3 className="font-bold text-gray-900 text-lg">{item.name}</h3>
                                                            <p className="text-amber-700 font-bold text-lg">₹{item.price}</p>
                                                        </div>
                                                        <div className="flex items-center gap-3">
                                                            <button onClick={() => updateQty(item._id, -1)} className="bg-amber-200 hover:bg-amber-300 w-9 h-9 rounded-full font-bold transition-colors">-</button>
                                                            <span className="font-bold text-lg w-8 text-center">{item.qty}</span>
                                                            <button onClick={() => updateQty(item._id, 1)} className="bg-amber-600 hover:bg-amber-700 text-white w-9 h-9 rounded-full font-bold transition-colors">+</button>
                                                        </div>
                                                        <button onClick={() => removeFromCart(item._id)} className="text-red-500 hover:bg-red-50 p-2 rounded-lg transition-colors">
                                                            <X size={20} />
                                                        </button>
                                                    </div>
                                                    {violated && (
                                                        <div className="mt-2 flex items-center gap-1.5 text-red-600 text-xs font-semibold">
                                                            <AlertTriangle size={13} />
                                                            Minimum order for {item.name} is {min} pcs —{' '}
                                                            <button
                                                                type="button"
                                                                onClick={() => updateQty(item._id, min - item.qty)}
                                                                className="underline hover:text-red-800 transition-colors"
                                                            >
                                                                add {min - item.qty} more
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                                );
                                            })
                                        )}
                                    </div>

                                    {cart.length > 0 && (
                                        <>
                                            {/* Order Type Selection */}
                                            <div className="grid grid-cols-2 gap-4 mb-6">
                                                <button
                                                    type="button"
                                                    onClick={() => setOrderType('delivery')}
                                                    className={`flex items-center justify-center gap-2 py-3 rounded-xl font-bold transition-all ${orderType === 'delivery' ? 'bg-amber-600 text-white shadow-lg' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                                >
                                                    <Truck size={20} />
                                                    Delivery
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setOrderType('pickup')}
                                                    className={`flex items-center justify-center gap-2 py-3 rounded-xl font-bold transition-all ${orderType === 'pickup' ? 'bg-amber-600 text-white shadow-lg' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                                >
                                                    <MapPin size={20} />
                                                    Pickup
                                                </button>
                                            </div>

                                            {/* Pickup Address Banner */}
                                            {orderType === 'pickup' && (
                                                <div className="rounded-2xl border border-amber-200 bg-amber-50 overflow-hidden">
                                                    <div className="flex items-center gap-2 bg-amber-600 px-4 py-2.5">
                                                        <MapPin size={16} className="text-white shrink-0" />
                                                        <span className="text-white font-bold text-sm tracking-wide uppercase">Pickup Location</span>
                                                    </div>
                                                    <div className="px-4 py-3 space-y-1">
                                                        <p className="text-gray-900 font-bold text-sm">Nayan's Kitchen & Delights</p>
                                                        <p className="text-gray-700 text-sm leading-relaxed">
                                                            5 Lower Rowden Street, Akashdeep Building,<br />
                                                            A Block, 2nd Floor, Flat No. 4
                                                        </p>
                                                        <p className="text-gray-600 text-sm">Near JD Birla College, AJC Bose Road</p>
                                                        <p className="text-gray-600 text-sm font-semibold">Kolkata – 700020, West Bengal</p>
                                                        <div className="pt-2 border-t border-amber-200 mt-2 flex items-center gap-2">
                                                            <Phone size={13} className="text-amber-600 shrink-0" />
                                                            <span className="text-gray-500 text-xs">Call before pickup:</span>
                                                            <a href="tel:+919975512357" className="text-amber-700 font-bold text-xs hover:underline">+91 99755 12357</a>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Customer Form */}
                                            <form onSubmit={handleOrder} className="space-y-6">
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                    <div>
                                                        <label className="block text-sm font-semibold text-gray-700 mb-2">Full Name *</label>
                                                        <input
                                                            required
                                                            placeholder="John Doe"
                                                            className="w-full px-4 py-3 border-2 border-amber-200 rounded-xl focus:border-amber-600 focus:ring-2 focus:ring-amber-200 outline-none transition-all"
                                                            value={customer.name}
                                                            onChange={e => setCustomer({ ...customer, name: e.target.value })}
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-semibold text-gray-700 mb-2">Phone Number *</label>
                                                        <input
                                                            required
                                                            placeholder="9876543210"
                                                            className="w-full px-4 py-3 border-2 border-amber-200 rounded-xl focus:border-amber-600 focus:ring-2 focus:ring-amber-200 outline-none transition-all"
                                                            value={customer.phone}
                                                            onChange={e => setCustomer({ ...customer, phone: e.target.value })}
                                                        />
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Email *</label>
                                                    <input
                                                        type="email"
                                                        required
                                                        placeholder="john@example.com"
                                                        className="w-full px-4 py-3 border-2 border-amber-200 rounded-xl focus:border-amber-600 focus:ring-2 focus:ring-amber-200 outline-none transition-all"
                                                        value={customer.email}
                                                        onChange={e => setCustomer({ ...customer, email: e.target.value })}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Delivery Address *</label>
                                                    <textarea
                                                        required
                                                        placeholder="123 Main St, Apt 4B, Mumbai, Maharashtra"
                                                        className="w-full px-4 py-3 border-2 border-amber-200 rounded-xl focus:border-amber-600 focus:ring-2 focus:ring-amber-200 outline-none transition-all"
                                                        rows="3"
                                                        value={customer.address}
                                                        onChange={e => setCustomer({ ...customer, address: e.target.value })}
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Pin Code *</label>
                                                    <input
                                                        type="text"
                                                        required
                                                        placeholder="400001"
                                                        className="w-full px-4 py-3 border-2 border-amber-200 rounded-xl focus:border-amber-600 focus:ring-2 focus:ring-amber-200 outline-none transition-all"
                                                        value={customer.pincode}
                                                        onChange={e => setCustomer({ ...customer, pincode: e.target.value })}
                                                    />
                                                </div>
                                                {orderType === 'delivery' && (
                                                    <div>
                                                        <label className="block text-sm font-semibold text-gray-700 mb-2">Delivery Time Slot *</label>
                                                        <select
                                                            required
                                                            className="w-full px-4 py-3 border-2 border-amber-200 rounded-xl focus:border-amber-600 focus:ring-2 focus:ring-amber-200 outline-none transition-all bg-white"
                                                            value={customer.deliveryTimeSlot}
                                                            onChange={e => setCustomer({ ...customer, deliveryTimeSlot: e.target.value })}
                                                        >
                                                            <option value="">Select a time slot</option>
                                                            {deliveryTimeSlots.map(slot => (
                                                                <option key={slot} value={slot}>{slot}</option>
                                                            ))}
                                                        </select>
                                                        <p className="text-xs text-gray-500 mt-1.5">Delivery slots are available for tomorrow (T+1), 10 AM – 8 PM.</p>
                                                    </div>
                                                )}
                                                <div>
                                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Special Instructions (Optional)</label>
                                                    <textarea
                                                        rows={3}
                                                        placeholder="Any allergy notes or cooking instructions?"
                                                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none transition-all resize-none"
                                                        value={customer.instructions}
                                                        onChange={e => setCustomer({ ...customer, instructions: e.target.value })}
                                                    />
                                                </div>

                                                <div className="bg-gradient-to-br from-amber-50 to-orange-100 p-6 rounded-2xl border-2 border-amber-200">
                                                    <div className="flex justify-between items-center mb-2">
                                                        <span className="text-gray-700 font-semibold">Subtotal</span>
                                                        <span className="text-gray-900 font-bold text-lg">₹{subtotal.toFixed(2)}</span>
                                                    </div>
                                                    <div className="flex justify-between items-center mb-4">
                                                        <span className="text-gray-700 font-semibold">Delivery</span>
                                                        <span className={orderType === 'delivery' ? "text-gray-900 font-bold" : "text-green-600 font-bold"}>
                                                            {orderType === 'delivery' ? '₹99.00' : 'FREE'}
                                                        </span>
                                                    </div>
                                                    <div className="border-t-2 border-amber-300 pt-4 flex justify-between items-center">
                                                        <span className="text-xl font-bold text-gray-900">Total</span>
                                                        <span className="text-3xl font-bold text-amber-700">₹{total.toFixed(2)}</span>
                                                    </div>
                                                </div>

                                                {/* Payment Method Selection */}
                                                <div className="bg-white p-6 rounded-2xl border-2 border-gray-100 shadow-sm">
                                                    <h3 className="font-bold text-gray-900 mb-4 text-lg">Payment Method</h3>
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">


                                                        {paymentConfig.isActive && paymentConfig.provider === 'Paytm' && (
                                                            <label className={`cursor-pointer border-2 rounded-xl p-4 flex items-center gap-3 transition-all ${customer.paymentMethod === 'Paytm' ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-amber-200'}`}>
                                                                <input
                                                                    type="radio"
                                                                    name="paymentMethod"
                                                                    value="Paytm"
                                                                    checked={customer.paymentMethod === 'Paytm'}
                                                                    onChange={() => setCustomer({ ...customer, paymentMethod: 'Paytm' })}
                                                                    className="w-5 h-5 text-amber-600 focus:ring-amber-500"
                                                                />
                                                                <span className="font-bold text-gray-800">Pay Online (Paytm)</span>
                                                                <div className="flex gap-1 ml-auto">
                                                                    <img src="https://upload.wikimedia.org/wikipedia/commons/2/24/Paytm_Logo_%28standalone%29.svg" className="h-6 object-contain" alt="Paytm" />
                                                                </div>
                                                            </label>
                                                        )}

                                                        {paymentConfig.isActive && paymentConfig.provider === 'Razorpay' && (
                                                            <label className={`cursor-pointer border-2 rounded-xl p-4 flex items-center gap-3 transition-all ${customer.paymentMethod === 'Razorpay' ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-amber-200'}`}>
                                                                <input
                                                                    type="radio"
                                                                    name="paymentMethod"
                                                                    value="Razorpay"
                                                                    checked={customer.paymentMethod === 'Razorpay'}
                                                                    onChange={() => setCustomer({ ...customer, paymentMethod: 'Razorpay' })}
                                                                    className="w-5 h-5 text-amber-600 focus:ring-amber-500"
                                                                />
                                                                <div className="flex-1">
                                                                    <span className="font-semibold text-gray-900 block">Pay with Razorpay</span>
                                                                    <span className="text-sm text-gray-500">Cards, UPI, NetBanking</span>
                                                                </div>
                                                            </label>
                                                        )}

                                                        {paymentConfig.isActive && paymentConfig.provider === 'PhonePe' && (
                                                            <label className={`cursor-pointer border-2 rounded-xl p-4 flex items-center gap-3 transition-all ${customer.paymentMethod === 'PhonePe' ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-amber-200'}`}>
                                                                <input
                                                                    type="radio"
                                                                    name="paymentMethod"
                                                                    value="PhonePe"
                                                                    checked={customer.paymentMethod === 'PhonePe'}
                                                                    onChange={() => setCustomer({ ...customer, paymentMethod: 'PhonePe' })}
                                                                    className="w-5 h-5 text-amber-600 focus:ring-amber-500"
                                                                />
                                                                <div className="flex-1">
                                                                    <span className="font-semibold text-gray-900 block">Pay with PhonePe</span>
                                                                    <span className="text-sm text-gray-500">UPI, Wallets, Cards</span>
                                                                </div>
                                                            </label>
                                                        )}

                                                        {paymentConfig.isActive && paymentConfig.provider === 'Cashfree' && (
                                                            <label className={`cursor-pointer border-2 rounded-xl p-4 flex items-center gap-3 transition-all ${customer.paymentMethod === 'Cashfree' ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-amber-200'}`}>
                                                                <input
                                                                    type="radio"
                                                                    name="paymentMethod"
                                                                    value="Cashfree"
                                                                    checked={customer.paymentMethod === 'Cashfree'}
                                                                    onChange={() => setCustomer({ ...customer, paymentMethod: 'Cashfree' })}
                                                                    className="w-5 h-5 text-amber-600 focus:ring-amber-500"
                                                                />
                                                                <div className="flex-1">
                                                                    <span className="font-semibold text-gray-900 block">Pay with Cashfree</span>
                                                                    <span className="text-sm text-gray-500">Cards, UPI, Wallets</span>
                                                                </div>
                                                            </label>
                                                        )}
                                                    </div>

                                                    {/* Notice for Online Payment SDK */}
                                                    {(customer.paymentMethod === 'Paytm' || customer.paymentMethod === 'Razorpay' || customer.paymentMethod === 'PhonePe' || customer.paymentMethod === 'Cashfree') && (
                                                        <div className="mt-6 animate-fadeIn">
                                                            <div className="bg-amber-50 border border-amber-200 p-4 rounded-xl text-center mb-4">
                                                                <p className="text-sm text-gray-600 font-medium">Clicking 'Place Order' will launch the secure {customer.paymentMethod} payment gateway.</p>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                {minQtyViolations.length > 0 && (
                                                    <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-3">
                                                        <AlertTriangle size={16} className="text-red-500 shrink-0" />
                                                        <p className="text-red-600 text-sm font-semibold">Please meet the minimum order quantities above before placing your order.</p>
                                                    </div>
                                                )}
                                                {minOrderViolation && (
                                                    <div className="flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 mb-3">
                                                        <AlertTriangle size={16} className="text-orange-500 shrink-0" />
                                                        <p className="text-orange-700 text-sm font-semibold">Minimum order value is ₹{minOrderValue}. Add ₹{(minOrderValue - subtotal).toFixed(2)} more to proceed.</p>
                                                    </div>
                                                )}
                                                <button
                                                    type="submit"
                                                    disabled={isSubmitting || minQtyViolations.length > 0 || minOrderViolation}
                                                    className={`w-full py-4 rounded-xl font-bold text-lg shadow-lg transform transition-all duration-200 flex items-center justify-center gap-2 ${
                                                        isSubmitting || minQtyViolations.length > 0 || minOrderViolation
                                                        ? 'bg-gray-300 cursor-not-allowed text-gray-500'
                                                        : 'bg-gradient-to-r from-amber-600 to-amber-700 text-white hover:shadow-xl hover:scale-105'
                                                        }`}
                                                >
                                                    {isSubmitting ? (
                                                        <>
                                                            <svg className="animate-spin h-5 w-5 mr-3 border-2 border-white border-t-transparent rounded-full" viewBox="0 0 24 24"></svg>
                                                            Placing Order...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <ShoppingBag size={20} />
                                                            Place Order (₹{total.toFixed(2)})
                                                        </>
                                                    )}
                                                </button>
                                                {submissionError && (
                                                    <div className="bg-red-50 border-2 border-red-100 p-4 rounded-xl mt-4 flex items-center gap-3 animate-shake">
                                                        <X size={20} className="text-red-500 shrink-0" />
                                                        <p className="text-red-600 font-medium text-sm">{submissionError}</p>
                                                    </div>
                                                )}
                                            </form>
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )
            }

            {/* Track Order Modal */}
            {
                showTrackOrder && (
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fadeIn">
                        <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full animate-slideUp overflow-hidden">
                            <div className="bg-gradient-to-r from-amber-600 to-amber-700 text-white p-6 flex justify-between items-center">
                                <h2 className="text-2xl font-bold">Track Your Order</h2>
                                <button onClick={() => setShowTrackOrder(false)} className="hover:bg-white/20 p-2 rounded-full transition-colors">
                                    <X size={24} />
                                </button>
                            </div>
                            <div className="p-6">
                                <form onSubmit={handleTrackOrder} className="flex gap-2 mb-6">
                                    <input
                                        type="text"
                                        placeholder="Enter Order ID"
                                        className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none transition-all"
                                        value={trackOrderId}
                                        onChange={(e) => setTrackOrderId(e.target.value)}
                                        required
                                    />
                                    <button type="submit" className="bg-amber-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-amber-700 transition-colors">
                                        Track
                                    </button>
                                </form>

                                {trackError && <p className="text-red-500 text-center font-medium mb-4">{trackError}</p>}

                                {trackResult && (
                                    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mt-4">
                                        <div className="flex justify-between items-center mb-4">
                                            <span className="text-gray-600 font-medium">Order Status</span>
                                            <span className={`px-3 py-1 rounded-full text-sm font-bold ${trackResult.status === 'Pending' ? 'bg-blue-100 text-blue-700' :
                                                trackResult.status === 'Accepted' ? 'bg-indigo-100 text-indigo-700' :
                                                    trackResult.status === 'Rejected' ? 'bg-red-100 text-red-700' :
                                                        'bg-green-100 text-green-700'
                                                }`}>
                                                {trackResult.status}
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center mb-4">
                                            <span className="text-gray-600 font-medium">Order Date</span>
                                            <span className="text-gray-900 font-bold">{new Date(trackResult.orderDate).toLocaleDateString()}</span>
                                        </div>
                                        {trackResult.total && (
                                            <div className="flex justify-between items-center">
                                                <span className="text-gray-600 font-medium">Total Amount</span>
                                                <span className="text-gray-900 font-bold">₹{trackResult.total}</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Item Detail Modal */}
            {
                selectedItemDetail && (
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fadeIn">
                        <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden animate-slideUp">
                            <div className="relative h-64 md:h-80">
                                <img
                                    src={selectedItemDetail.image}
                                    alt={selectedItemDetail.name}
                                    className="w-full h-full object-cover"
                                />
                                <button
                                    onClick={() => setSelectedItemDetail(null)}
                                    className="absolute top-4 right-4 bg-black/40 hover:bg-black/60 text-white p-2 rounded-full backdrop-blur-sm transition-all"
                                >
                                    <X size={24} />
                                </button>
                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6">
                                    <span className="bg-amber-100 text-amber-800 text-sm px-3 py-1 rounded-full font-bold mb-2 inline-block">
                                        {selectedItemDetail.category}
                                    </span>
                                    <h2 className="text-3xl font-bold text-white">{selectedItemDetail.name}</h2>
                                </div>
                            </div>

                            <div className="p-8">
                                <div className="flex flex-col gap-1 mb-6">
                                    <div className="flex justify-between items-center">
                                        <div className="text-4xl font-black text-amber-700">₹{selectedItemDetail.price}</div>
                                        {menuRatings[selectedItemDetail._id] && (
                                            <div className="text-right">
                                                <div className="flex items-center gap-1 text-amber-500 mb-1">
                                                    {[1, 2, 3, 4, 5].map((star) => (
                                                        <Star
                                                            key={star}
                                                            size={18}
                                                            className={star <= Math.round(menuRatings[selectedItemDetail._id].avg) ? "fill-amber-500" : "text-gray-200"}
                                                        />
                                                    ))}
                                                    <span className="text-lg font-bold ml-2 text-amber-700">{menuRatings[selectedItemDetail._id].avg}</span>
                                                </div>
                                                <p className="text-sm text-gray-500 font-medium">Based on {menuRatings[selectedItemDetail._id].count} customer reviews</p>
                                            </div>
                                        )}
                                    </div>
                                    {selectedItemDetail.shortDescription && (
                                        <div className="bg-amber-50 text-amber-700 px-4 py-2 rounded-xl font-bold flex items-center gap-2 border border-amber-100 italic w-fit">
                                            <Info size={18} className="text-amber-500" />
                                            {selectedItemDetail.shortDescription}
                                        </div>
                                    )}
                                </div>

                                {selectedItemDetail.description && (
                                    <div className="mb-8">
                                        <h3 className="text-gray-900 font-bold text-lg mb-2 flex items-center gap-2">
                                            <Info size={20} className="text-amber-600" />
                                            Description
                                        </h3>
                                        <p className="text-gray-600 text-lg leading-relaxed whitespace-pre-wrap">
                                            {selectedItemDetail.description}
                                        </p>
                                    </div>
                                )}

                                <div className="grid grid-cols-2 gap-4 text-center">
                                    {cart.find(c => c._id === selectedItemDetail._id) ? (
                                        <div className="flex items-center justify-between bg-amber-50 rounded-2xl p-1 border-2 border-amber-200">
                                            <button
                                                onClick={() => updateQty(selectedItemDetail._id, -1)}
                                                className="w-12 h-12 bg-white text-amber-700 rounded-xl shadow-sm flex items-center justify-center hover:bg-amber-100 transition-colors"
                                            >
                                                <Minus size={20} />
                                            </button>
                                            <span className="font-bold text-xl text-amber-900">{cart.find(c => c._id === selectedItemDetail._id).qty}</span>
                                            <button
                                                onClick={() => updateQty(selectedItemDetail._id, 1)}
                                                className="w-12 h-12 bg-amber-600 text-white rounded-xl shadow-sm flex items-center justify-center hover:bg-amber-700 transition-colors"
                                            >
                                                <Plus size={20} />
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            onClick={() => addToCart(selectedItemDetail)}
                                            className="bg-gradient-to-r from-amber-600 to-amber-700 text-white font-bold py-4 rounded-2xl shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2 text-lg active:scale-95"
                                        >
                                            <Plus size={22} />
                                            Add
                                        </button>
                                    )}
                                    <button
                                        onClick={() => setSelectedItemDetail(null)}
                                        className="bg-gray-100 text-gray-700 font-bold py-4 rounded-2xl hover:bg-gray-200 transition-all text-lg active:scale-95"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* ───── Payment Error Modal ───── */}
            {paymentError && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(6px)' }}>
                    <div
                        className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden"
                        style={{ animation: 'slideUp 0.3s ease' }}
                    >
                        {/* Top colored strip */}
                        <div className={`p-6 text-white text-center ${paymentError.title === 'Payment Cancelled'
                            ? 'bg-gradient-to-br from-orange-500 to-orange-600'
                            : paymentError.title.includes('Verification')
                                ? 'bg-gradient-to-br from-blue-600 to-blue-700'
                                : paymentError.title.includes('SDK') || paymentError.title.includes('Load')
                                    ? 'bg-gradient-to-br from-gray-700 to-gray-800'
                                    : 'bg-gradient-to-br from-red-500 to-red-600'
                            }`}>
                            <div className="flex justify-end mb-2">
                                <button
                                    onClick={() => { setPaymentError(null); setIsSubmitting(false); }}
                                    className="bg-white/20 hover:bg-white/30 p-1.5 rounded-full transition-colors"
                                >
                                    <X size={18} />
                                </button>
                            </div>
                            <div className="flex justify-center mb-4">
                                <div className="bg-white/20 p-4 rounded-full">
                                    {paymentError.title === 'Payment Cancelled' ? (
                                        <X size={36} />
                                    ) : paymentError.title.includes('Verification') ? (
                                        <WifiOff size={36} />
                                    ) : paymentError.title.includes('SDK') || paymentError.title.includes('Load') ? (
                                        <WifiOff size={36} />
                                    ) : (
                                        <ShieldX size={36} />
                                    )}
                                </div>
                            </div>
                            <h2 className="text-2xl font-black tracking-tight">{paymentError.title}</h2>
                            {paymentError.provider && (
                                <p className="text-white/70 text-sm mt-1 font-medium">via {paymentError.provider}</p>
                            )}
                        </div>

                        {/* Body */}
                        <div className="p-6">
                            <p className="text-gray-600 text-center leading-relaxed text-base mb-6">
                                {paymentError.message}
                            </p>

                            {/* Info pill */}
                            <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4 mb-6 flex items-start gap-3">
                                <AlertTriangle size={18} className="text-amber-500 shrink-0 mt-0.5" />
                                <p className="text-sm text-amber-800 leading-relaxed">
                                    <span className="font-bold">Your cart &amp; details are saved.</span> You don't need to fill anything again — just click <span className="font-bold">Try Again</span> below.
                                </p>
                            </div>

                            {/* Action buttons */}
                            <div className="flex flex-col gap-3">
                                {paymentError.canRetry && (
                                    <button
                                        onClick={() => {
                                            setPaymentError(null);
                                            setIsSubmitting(false);
                                            // Checkout stays open with all data preserved
                                        }}
                                        className="w-full py-3.5 rounded-2xl font-bold text-white text-base flex items-center justify-center gap-2 shadow-lg hover:shadow-xl active:scale-95 transition-all bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700"
                                    >
                                        <RefreshCw size={18} />
                                        Try Again
                                    </button>
                                )}

                                <button
                                    onClick={() => {
                                        setPaymentError(null);
                                        setIsSubmitting(false);
                                        setShowCheckout(false);
                                    }}
                                    className="text-gray-400 text-sm font-medium text-center hover:text-gray-600 transition-colors py-1"
                                >
                                    Cancel — I'll order later
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* ───── Auth Modal ───── */}
            {showAuthModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[60] animate-fadeIn">
                    <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden animate-slideUp">
                        <div className="bg-gradient-to-r from-amber-600 to-amber-700 p-8 text-white text-center">
                            <div className="bg-white/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                                {authMode === 'login' ? <LogIn size={32} /> : authMode === 'register' ? <UserPlus size={32} /> : <Lock size={32} />}
                            </div>
                            <h2 className="text-3xl font-bold">{authMode === 'login' ? 'Welcome Back' : authMode === 'register' ? 'Join Us' : 'Forgot Password?'}</h2>
                            <p className="text-amber-100 mt-2">
                                {authMode === 'login' ? 'Login to order your favorite Maharashtrian meals' : authMode === 'register' ? 'Create an account for a faster checkout experience' : "Enter your email and we'll send you a reset link"}
                            </p>
                        </div>

                        <div className="p-8">
                            {/* ── Forgot Password Form ── */}
                            {authMode === 'forgot' && (
                                forgotSent ? (
                                    <div className="text-center py-4">
                                        <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                                            <Check size={32} className="text-green-600" />
                                        </div>
                                        <h3 className="text-lg font-bold text-gray-900 mb-2">Check your inbox!</h3>
                                        <p className="text-gray-500 text-sm mb-6">If your email is registered, a password reset link has been sent. Check your inbox (and spam folder).</p>
                                        <button onClick={() => { setAuthMode('login'); setForgotSent(false); setAuthError(''); }} className="w-full bg-amber-600 text-white py-3 rounded-xl font-bold hover:bg-amber-700 transition-all">Back to Login</button>
                                    </div>
                                ) : (
                                    <form onSubmit={handleForgotPassword} className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-bold text-gray-700 mb-1">Registered Email</label>
                                            <input
                                                type="email"
                                                required
                                                className="w-full px-4 py-3 border-2 border-gray-100 rounded-xl focus:border-amber-500 outline-none transition-all"
                                                placeholder="john@example.com"
                                                value={authForm.email}
                                                onChange={e => setAuthForm({ ...authForm, email: e.target.value })}
                                            />
                                        </div>
                                        {authError && <p className="text-red-500 text-sm font-bold text-center">{authError}</p>}
                                        <button type="submit" disabled={authLoading} className="w-full bg-amber-600 text-white py-4 rounded-xl font-bold text-lg hover:bg-amber-700 transition-all disabled:bg-gray-400 mt-2">
                                            {authLoading ? 'Sending...' : 'Send Reset Link'}
                                        </button>
                                        <button type="button" onClick={() => { setAuthMode('login'); setAuthError(''); }} className="w-full py-3 text-gray-400 font-bold hover:text-gray-600 border-2 border-gray-100 rounded-xl mt-1">
                                            Back to Login
                                        </button>
                                    </form>
                                )
                            )}

                            {/* ── Login / Register Form ── */}
                            {authMode !== 'forgot' && (
                                <>
                                <form onSubmit={handleAuthSubmit} className="space-y-4">
                                    {authMode === 'register' && (
                                        <>
                                            <div>
                                                <label className="block text-sm font-bold text-gray-700 mb-1">Full Name</label>
                                                <input
                                                    required
                                                    className="w-full px-4 py-3 border-2 border-gray-100 rounded-xl focus:border-amber-500 outline-none transition-all"
                                                    placeholder="John Doe"
                                                    value={authForm.name}
                                                    onChange={e => setAuthForm({ ...authForm, name: e.target.value })}
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-bold text-gray-700 mb-1">Phone Number</label>
                                                <input
                                                    required
                                                    className="w-full px-4 py-3 border-2 border-gray-100 rounded-xl focus:border-amber-500 outline-none transition-all"
                                                    placeholder="9876543210"
                                                    value={authForm.phone}
                                                    onChange={e => setAuthForm({ ...authForm, phone: e.target.value })}
                                                />
                                            </div>
                                        </>
                                    )}
                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-1">Email Address</label>
                                        <input
                                            type="email"
                                            required
                                            className="w-full px-4 py-3 border-2 border-gray-100 rounded-xl focus:border-amber-500 outline-none transition-all"
                                            placeholder="john@example.com"
                                            value={authForm.email}
                                            onChange={e => setAuthForm({ ...authForm, email: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <div className="flex justify-between items-center mb-1">
                                            <label className="block text-sm font-bold text-gray-700">Password</label>
                                            {authMode === 'login' && (
                                                <button type="button" onClick={() => { setAuthMode('forgot'); setAuthError(''); setForgotSent(false); }} className="text-xs text-amber-700 font-bold hover:underline">
                                                    Forgot Password?
                                                </button>
                                            )}
                                        </div>
                                        <input
                                            type="password"
                                            required
                                            className="w-full px-4 py-3 border-2 border-gray-100 rounded-xl focus:border-amber-500 outline-none transition-all"
                                            placeholder="••••••••"
                                            value={authForm.password}
                                            onChange={e => setAuthForm({ ...authForm, password: e.target.value })}
                                        />
                                    </div>
                                    {authMode === 'register' && (
                                        <div>
                                            <label className="block text-sm font-bold text-gray-700 mb-1">Confirm Password</label>
                                            <input
                                                type="password"
                                                required
                                                className="w-full px-4 py-3 border-2 border-gray-100 rounded-xl focus:border-amber-500 outline-none transition-all"
                                                placeholder="••••••••"
                                                value={authForm.confirmPassword}
                                                onChange={e => setAuthForm({ ...authForm, confirmPassword: e.target.value })}
                                            />
                                        </div>
                                    )}

                                    {authError && <p className="text-red-500 text-sm font-bold text-center">{authError}</p>}

                                    <button
                                        type="submit"
                                        disabled={authLoading}
                                        className="w-full bg-amber-600 text-white py-4 rounded-xl font-bold text-lg shadow-lg hover:bg-amber-700 transition-all active:scale-95 disabled:bg-gray-400 mt-4"
                                    >
                                        {authLoading ? 'Please wait...' : (authMode === 'login' ? 'Login' : 'Create Account')}
                                    </button>
                                </form>

                                <div className="my-6 flex items-center gap-3">
                                    <div className="h-px bg-gray-200 flex-1"></div>
                                    <span className="text-xs text-gray-400 font-bold uppercase tracking-wider">or</span>
                                    <div className="h-px bg-gray-200 flex-1"></div>
                                </div>

                                <button
                                    type="button"
                                    onClick={handleGoogleSignIn}
                                    disabled={googleLoading || authLoading}
                                    className="w-full bg-white text-gray-700 py-4 rounded-xl font-bold text-lg border-2 border-gray-100 shadow-sm hover:bg-gray-50 hover:border-gray-200 transition-all active:scale-95 disabled:opacity-60 disabled:active:scale-100 flex items-center justify-center gap-3"
                                >
                                    {googleLoading ? (
                                        <>
                                            <RefreshCw size={20} className="animate-spin text-amber-600" />
                                            Connecting...
                                        </>
                                    ) : (
                                        <>
                                            <svg width="22" height="22" viewBox="0 0 48 48" aria-hidden="true">
                                                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
                                                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
                                                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24s.92 7.54 2.56 10.78l7.97-6.19z" />
                                                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
                                            </svg>
                                            Continue with Google
                                        </>
                                    )}
                                </button>

                                <div className="mt-6 text-center">
                                    <p className="text-gray-500">
                                        {authMode === 'login' ? "Don't have an account?" : "Already have an account?"}
                                        <button
                                            onClick={() => { setAuthMode(authMode === 'login' ? 'register' : 'login'); setAuthError(''); }}
                                            className="ml-2 text-amber-700 font-bold hover:underline"
                                        >
                                            {authMode === 'login' ? 'Sign Up' : 'Login'}
                                        </button>
                                    </p>
                                    <button
                                        onClick={() => setShowAuthModal(false)}
                                        className="mt-6 w-full py-3 text-gray-400 font-bold hover:text-gray-600 transition-all border-2 border-gray-100 rounded-xl flex items-center justify-center gap-2 hover:bg-gray-50"
                                    >
                                        <X size={18} /> Cancel
                                    </button>
                                </div>
                                </>
                            )}
                        </div>

                        <button
                            onClick={() => setShowAuthModal(false)}
                            className="absolute top-4 right-4 text-white/70 hover:text-white"
                        >
                            <X size={24} />
                        </button>
                    </div>
                </div>
            )}

            {/* ───── Feedback Modal ───── */}
            {showFeedbackModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[60] animate-fadeIn">
                    <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full overflow-hidden animate-slideUp">
                        <div className="bg-gradient-to-r from-amber-600 to-amber-700 p-8 text-white text-center">
                            <Star className="mx-auto mb-4 fill-amber-300 text-amber-300" size={48} />
                            <h2 className="text-3xl font-bold">Rate Your Meal</h2>
                            <p className="text-amber-100 mt-2">How was your experience with Nayan's Kitchen & Delights?</p>
                        </div>

                        <div className="p-8">
                            {feedbackDone ? (
                                <div className="text-center py-8">
                                    <div className="bg-green-100 text-green-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <Check size={32} />
                                    </div>
                                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h3>
                                    <p className="text-gray-600 mb-6">Your feedback helps us improve our service.</p>
                                    <button
                                        onClick={() => setShowFeedbackModal(false)}
                                        className="bg-amber-600 text-white px-8 py-3 rounded-xl font-bold hover:bg-amber-700 transition-all"
                                    >
                                        Close
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={handleFeedbackSubmit} className="space-y-6">
                                    <div className="space-y-4 max-h-60 overflow-y-auto pr-2">
                                        {(feedbackOrder?.items || cart).map((item, idx) => (
                                            <div key={idx} className="flex flex-col gap-2 p-3 bg-amber-50 rounded-xl">
                                                <span className="font-bold text-gray-800">{item.name}</span>
                                                <div className="flex gap-2">
                                                    {[1, 2, 3, 4, 5].map(star => (
                                                        <button
                                                            key={star}
                                                            type="button"
                                                            onClick={() => setFeedbackRatings({ ...feedbackRatings, [item.menuItem || item._id]: star })}
                                                            className="transition-transform active:scale-90"
                                                        >
                                                            <Star
                                                                size={24}
                                                                className={star <= (feedbackRatings[item.menuItem || item._id] || 0) ? "fill-amber-500 text-amber-500" : "text-gray-300"}
                                                            />
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2">Your Comments</label>
                                        <textarea
                                            className="w-full px-4 py-3 border-2 border-gray-100 rounded-xl focus:border-amber-500 outline-none transition-all resize-none"
                                            rows="3"
                                            placeholder="What did you like the most?"
                                            value={feedbackComment}
                                            onChange={e => setFeedbackComment(e.target.value)}
                                        />
                                    </div>

                                    {!currentUser && (
                                        <div>
                                            <label className="block text-sm font-bold text-gray-700 mb-2">Your Name</label>
                                            <input
                                                required
                                                className="w-full px-4 py-3 border-2 border-gray-100 rounded-xl focus:border-amber-500 outline-none transition-all"
                                                placeholder="Enter your name"
                                                value={feedbackName}
                                                onChange={e => setFeedbackName(e.target.value)}
                                            />
                                        </div>
                                    )}

                                    <button
                                        type="submit"
                                        disabled={feedbackSubmitting || Object.keys(feedbackRatings).length === 0}
                                        className="w-full bg-amber-600 text-white py-4 rounded-xl font-bold text-lg shadow-lg hover:bg-amber-700 transition-all disabled:bg-gray-400"
                                    >
                                        {feedbackSubmitting ? 'Submitting...' : 'Submit Review'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setShowFeedbackModal(false)}
                                        className="w-full mt-4 py-3 text-gray-400 font-bold hover:text-gray-600 transition-all border-2 border-gray-100 rounded-xl flex items-center justify-center gap-2 hover:bg-gray-50"
                                    >
                                        <X size={18} /> Cancel
                                    </button>
                                </form>
                            )}
                        </div>

                        <button
                            onClick={() => setShowFeedbackModal(false)}
                            className="absolute top-4 right-4 text-white/70 hover:text-white"
                        >
                            <X size={24} />
                        </button>
                    </div>
                </div>
            )}
            {/* ───── Floating Support Button ───── */}
            <button
                onClick={() => setShowSupportModal(true)}
                className="fixed bottom-56 right-6 bg-amber-600 text-white p-4 rounded-full shadow-2xl hover:bg-amber-700 transition-all hover:scale-110 z-[50] flex items-center gap-2 group"
            >
                <MessageSquare size={24} />
                <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 font-bold">Support</span>
            </button>

            {/* ───── Floating Phone Button ───── */}
            <a
                href="tel:+919975512357"
                className="fixed bottom-40 right-6 bg-blue-600 text-white p-4 rounded-full shadow-2xl hover:bg-blue-700 transition-all hover:scale-110 z-[50] flex items-center gap-2 group"
            >
                <Phone size={24} />
                <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 font-bold">Call</span>
            </a>

            {/* ───── Floating WhatsApp Button ───── */}
            <a
                href="https://api.whatsapp.com/send/?phone=919975512357"
                target="_blank"
                rel="noopener noreferrer"
                className="fixed bottom-24 right-6 bg-[#25D366] text-white p-4 rounded-full shadow-2xl hover:bg-[#128C7E] transition-all hover:scale-110 z-[50] flex items-center gap-2 group animate-bounce-subtle"
            >
                <svg viewBox="0 0 24 24" width="24" height="24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                <span className="max-w-0 overflow-hidden group-hover:max-w-xs transition-all duration-300 font-bold">WhatsApp</span>
            </a>

            {/* ───── Support Modal ───── */}
            {showSupportModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[60] animate-fadeIn">
                    <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden animate-slideUp">
                        <div className="bg-gradient-to-r from-amber-600 to-amber-700 p-8 text-white text-center">
                            <MessageSquare className="mx-auto mb-4" size={48} />
                            <h2 className="text-3xl font-bold">Support Desk</h2>
                            <p className="text-amber-100 mt-2">Have a query? We're here to help!</p>
                        </div>
                        <div className="p-8">
                            {supportDone ? (
                                <div className="text-center py-8">
                                    <div className="bg-green-100 text-green-600 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                                        <Check size={32} />
                                    </div>
                                    <h3 className="text-2xl font-bold text-gray-900 mb-2">Message Sent!</h3>
                                    <p className="text-gray-600">Our team will get back to you shortly.</p>
                                </div>
                            ) : (
                                <form onSubmit={handleSupportSubmit} className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-1">Name</label>
                                        <input
                                            required
                                            className="w-full px-4 py-3 border-2 border-gray-100 rounded-xl focus:border-amber-500 outline-none transition-all"
                                            value={supportForm.name}
                                            onChange={e => setSupportForm({ ...supportForm, name: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-1">Email</label>
                                        <input
                                            type="email"
                                            required
                                            className="w-full px-4 py-3 border-2 border-gray-100 rounded-xl focus:border-amber-500 outline-none transition-all"
                                            value={supportForm.email}
                                            onChange={e => setSupportForm({ ...supportForm, email: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-1">Message</label>
                                        <textarea
                                            required
                                            rows="3"
                                            className="w-full px-4 py-3 border-2 border-gray-100 rounded-xl focus:border-amber-500 outline-none transition-all resize-none"
                                            value={supportForm.message}
                                            onChange={e => setSupportForm({ ...supportForm, message: e.target.value })}
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        disabled={supportLoading}
                                        className="w-full bg-amber-600 text-white py-4 rounded-xl font-bold text-lg shadow-lg hover:bg-amber-700 transition-all"
                                    >
                                        {supportLoading ? 'Sending...' : 'Send Message'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setShowSupportModal(false)}
                                        className="w-full mt-4 py-3 text-gray-400 font-bold hover:text-gray-600 transition-all border-2 border-gray-100 rounded-xl flex items-center justify-center gap-2 hover:bg-gray-50"
                                    >
                                        <X size={18} /> Cancel
                                    </button>
                                </form>
                            )}
                        </div>
                        <button onClick={() => setShowSupportModal(false)} className="absolute top-4 right-4 text-white/70 hover:text-white">
                            <X size={24} />
                        </button>
                    </div>
                </div>
            )}

            {/* ───── Order History Modal ───── */}
            {showHistoryModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[60] animate-fadeIn">
                    <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col animate-slideUp">
                        <div className="bg-gradient-to-r from-amber-600 to-amber-700 p-6 text-white flex justify-between items-center">
                            <div className="flex items-center gap-3">
                                <Package size={32} />
                                <div>
                                    <h2 className="text-2xl font-bold">My Order History</h2>
                                    <p className="text-amber-100 text-sm">Track and manage your past orders</p>
                                </div>
                            </div>
                            <button onClick={() => setShowHistoryModal(false)} className="bg-white/20 p-2 rounded-full hover:bg-white/30 transition-colors">
                                <X size={24} />
                            </button>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
                            {loadingHistory ? (
                                <div className="flex flex-col items-center justify-center h-64">
                                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-amber-500 border-t-transparent mb-4"></div>
                                    <p className="text-gray-500 font-medium">Fetching your orders...</p>
                                </div>
                            ) : customerOrders.length === 0 ? (
                                <div className="text-center py-20">
                                    <ShoppingBag className="mx-auto text-gray-200 mb-4" size={64} />
                                    <h3 className="text-xl font-bold text-gray-900 mb-2">No Orders Yet</h3>
                                    <p className="text-gray-500 mb-6">Looks like you haven't placed any orders yet.</p>
                                    <button onClick={() => { setShowHistoryModal(false); scrollToSection('menu'); }} className="bg-amber-600 text-white px-8 py-3 rounded-xl font-bold hover:bg-amber-700 transition-all">
                                        Start Ordering
                                    </button>
                                </div>
                            ) : (
                                <div className="space-y-4">
                                    {customerOrders.map(order => (
                                        <div key={order._id} className="bg-white border border-gray-100 rounded-2xl shadow-sm p-5 hover:shadow-md transition-shadow">
                                            <div className="flex flex-wrap justify-between items-start gap-4 mb-4">
                                                <div>
                                                    <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-1">Order ID: #{order._id.slice(-6).toUpperCase()}</p>
                                                    <p className="text-lg font-bold text-gray-900">{new Date(order.orderDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-2xl font-black text-amber-600">₹{order.total.toFixed(2)}</p>
                                                    <span className={`inline-flex px-3 py-1 rounded-full text-xs font-bold ${order.status === 'Delivered' || order.status === 'Fulfilled' ? 'bg-green-100 text-green-700' : order.status === 'Rejected' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                                                        {order.status || 'Pending'}
                                                    </span>
                                                </div>
                                            </div>

                                            <div className="border-t border-gray-100 pt-4 flex flex-wrap justify-between items-center gap-4">
                                                <div className="flex -space-x-2">
                                                    {order.items.slice(0, 3).map((item, idx) => (
                                                        <div key={idx} className="w-10 h-10 rounded-full bg-amber-100 border-2 border-white flex items-center justify-center text-[10px] font-bold text-amber-700" title={item.name}>
                                                            {item.name.charAt(0)}
                                                        </div>
                                                    ))}
                                                    {order.items.length > 3 && (
                                                        <div className="w-10 h-10 rounded-full bg-gray-100 border-2 border-white flex items-center justify-center text-[10px] font-bold text-gray-600">
                                                            +{order.items.length - 3}
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex gap-2">
                                                    <button
                                                        onClick={() => setSelectedInvoice(order)}
                                                        className="flex items-center gap-2 px-4 py-2 border-2 border-amber-100 text-amber-700 rounded-xl font-bold text-sm hover:bg-amber-50 transition-all"
                                                    >
                                                        <Search size={16} />
                                                        Details
                                                    </button>
                                                    {isInvoiceReady(order) ? (
                                                        <button
                                                            onClick={() => setSelectedInvoice(order)}
                                                            className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-xl font-bold text-sm hover:bg-amber-700 shadow-md active:scale-95 transition-all"
                                                        >
                                                            <Download size={16} />
                                                            Invoice
                                                        </button>
                                                    ) : (
                                                        <span className="flex items-center gap-2 px-4 py-2 bg-gray-50 text-gray-400 border-2 border-gray-100 rounded-xl font-bold text-sm" title="Invoice is generated once payment is confirmed">
                                                            <Lock size={14} />
                                                            Payment {order.paymentStatus || 'Pending'}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ───── Invoice / Details Modal ───── */}
            {selectedInvoice && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 z-[70] animate-fadeIn">
                    <div id="printable-bill" className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden flex flex-col animate-scaleIn">
                        <div className="p-8 border-b-2 border-dashed border-gray-200">
                            <div className="flex justify-between items-start mb-6">
                                <div className="flex items-center gap-2">
                                    <div className="bg-amber-600 p-1.5 rounded-lg">
                                        <img
                                            src="/logo.png"
                                            alt="Nayan's Kitchen Logo"
                                            className="w-16 h-16 object-contain rounded-2xl"
                                        />
                                    </div>
                                    <h1 className="text-xl font-black text-gray-900 uppercase tracking-tighter">Nayan's Kitchen & Delights</h1>
                                </div>
                                <div className="text-right">
                                    <p className="text-xs font-bold text-gray-400 uppercase">{isInvoiceReady(selectedInvoice) ? 'Invoice' : 'Order Summary'}</p>
                                    <p className="text-sm font-black text-gray-900">#{selectedInvoice._id.slice(-6).toUpperCase()}</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                    <p className="text-gray-400 font-bold uppercase text-[10px] mb-1">Date</p>
                                    <p className="font-bold text-gray-800">{new Date(selectedInvoice.orderDate).toLocaleDateString()}</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-gray-400 font-bold uppercase text-[10px] mb-1">Method</p>
                                    <p className="font-bold text-gray-800">{selectedInvoice.deliveryMethod}</p>
                                </div>
                                <div>
                                    <p className="text-gray-400 font-bold uppercase text-[10px] mb-1">Order Status</p>
                                    <p className="font-bold text-gray-800">{selectedInvoice.status || 'Pending'}</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-gray-400 font-bold uppercase text-[10px] mb-1">Payment</p>
                                    <p className={`font-bold ${isInvoiceReady(selectedInvoice) ? 'text-green-600' : 'text-amber-600'}`}>
                                        {selectedInvoice.paymentStatus || 'Pending'}
                                        {selectedInvoice.paymentMethod ? ` · ${selectedInvoice.paymentMethod}` : ''}
                                    </p>
                                </div>
                            </div>

                            {selectedInvoice.transactionId && (
                                <div className="mt-4 pt-4 border-t border-gray-100">
                                    <p className="text-gray-400 font-bold uppercase text-[10px] mb-1">Transaction Reference</p>
                                    <p className="font-mono text-xs font-bold text-gray-800 break-all">{selectedInvoice.transactionId}</p>
                                </div>
                            )}

                            {selectedInvoice.refundId && (
                                <div className="mt-3">
                                    <p className="text-gray-400 font-bold uppercase text-[10px] mb-1">Refund Reference</p>
                                    <p className="font-mono text-xs font-bold text-gray-800 break-all">{selectedInvoice.refundId}</p>
                                </div>
                            )}
                        </div>

                        <div className="p-8 flex-1">
                            <div className="space-y-4 mb-8">
                                {selectedInvoice.items.map((item, idx) => (
                                    <div key={idx} className="flex justify-between items-center text-sm">
                                        <div className="flex items-center gap-3">
                                            <span className="w-6 h-6 bg-gray-100 rounded flex items-center justify-center font-bold text-gray-500 text-xs">{item.quantity}x</span>
                                            <span className="font-bold text-gray-800">{item.name}</span>
                                        </div>
                                        <span className="font-bold text-gray-900">₹{(item.price * item.quantity).toFixed(2)}</span>
                                    </div>
                                ))}
                            </div>

                            <div className="space-y-2 pt-4 border-t border-gray-100">
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-500 font-medium">Subtotal</span>
                                    <span className="font-bold text-gray-900">₹{(selectedInvoice.total - (selectedInvoice.deliveryFee || 0)).toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-gray-500 font-medium">Delivery</span>
                                    <span className="font-bold text-gray-900">₹{(selectedInvoice.deliveryFee || 0).toFixed(2)}</span>
                                </div>
                                <div className="flex justify-between items-center pt-4 mt-2 border-t-2 border-amber-600">
                                    <span className="text-lg font-black text-gray-900">{isInvoiceReady(selectedInvoice) ? 'Total Paid' : 'Amount Payable'}</span>
                                    <span className="text-2xl font-black text-amber-600">₹{selectedInvoice.total.toFixed(2)}</span>
                                </div>
                            </div>

                            {!isInvoiceReady(selectedInvoice) && (
                                <div className="mt-6 bg-amber-50 border-2 border-amber-100 rounded-xl p-4 flex items-start gap-3">
                                    <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
                                    <p className="text-sm text-amber-800 font-medium">
                                        This is an order summary, not an invoice. A downloadable invoice is generated once your payment is confirmed.
                                    </p>
                                </div>
                            )}
                        </div>

                        <div className="p-6 bg-gray-50 border-t border-gray-100 flex gap-3 no-print">
                            {isInvoiceReady(selectedInvoice) && (
                                <button
                                    onClick={() => window.print()}
                                    className="flex-1 bg-gray-900 text-white py-3 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-black transition-all"
                                >
                                    <Download size={18} />
                                    Print / Save PDF
                                </button>
                            )}
                            <button
                                onClick={() => setSelectedInvoice(null)}
                                className={`py-3 border-2 border-gray-200 text-gray-600 rounded-xl font-bold hover:bg-gray-100 transition-all ${isInvoiceReady(selectedInvoice) ? 'px-6' : 'flex-1'}`}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ───── Toast Notification ───── */}
            {toast.show && (
                <div className={`fixed top-6 right-6 z-[100] flex items-center gap-3 px-6 py-4 rounded-2xl shadow-2xl animate-slideInRight ${toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'} text-white font-bold`}>
                    {toast.type === 'success' ? <Check size={20} /> : <AlertTriangle size={20} />}
                    {toast.message}
                </div>
            )}
            {/* Chatbot removed — responses were not accurate enough */}

            {/* ───── Profile Update Modal ───── */}
            {showProfileModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-[60] animate-fadeIn">
                    <div className="bg-white rounded-3xl shadow-2xl max-w-xl w-full overflow-hidden animate-slideUp flex flex-col max-h-[90vh]">
                        <div className="bg-gradient-to-r from-amber-600 to-amber-700 p-8 text-white">
                            <div className="flex justify-between items-center">
                                <div className="flex items-center gap-4">
                                    <div className="bg-white/20 p-3 rounded-2xl">
                                        <User size={32} />
                                    </div>
                                    <div>
                                        <h2 className="text-2xl font-bold">Manage Profile</h2>
                                        <p className="text-amber-100 text-sm">Update your personal and delivery details</p>
                                    </div>
                                </div>
                                <button onClick={() => setShowProfileModal(false)} className="hover:bg-white/20 p-2 rounded-full transition-colors">
                                    <X size={24} />
                                </button>
                            </div>
                        </div>

                        <div className="p-8 overflow-y-auto">
                            <form onSubmit={handleProfileUpdate} className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2">Full Name</label>
                                        <input
                                            required
                                            className="w-full px-4 py-3 border-2 border-gray-100 rounded-xl focus:border-amber-500 outline-none transition-all"
                                            value={profileForm.name}
                                            onChange={e => setProfileForm({ ...profileForm, name: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2">Phone Number</label>
                                        <input
                                            required
                                            className="w-full px-4 py-3 border-2 border-gray-100 rounded-xl focus:border-amber-500 outline-none transition-all"
                                            value={profileForm.phone}
                                            onChange={e => setProfileForm({ ...profileForm, phone: e.target.value })}
                                        />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-bold text-gray-700 mb-2">Email Address</label>
                                        <input
                                            type="email"
                                            required
                                            className="w-full px-4 py-3 border-2 border-gray-100 rounded-xl focus:border-amber-500 outline-none transition-all"
                                            value={profileForm.email}
                                            onChange={e => setProfileForm({ ...profileForm, email: e.target.value })}
                                        />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-bold text-gray-700 mb-2">Delivery Address</label>
                                        <textarea
                                            rows="2"
                                            className="w-full px-4 py-3 border-2 border-gray-100 rounded-xl focus:border-amber-500 outline-none transition-all resize-none"
                                            value={profileForm.address}
                                            onChange={e => setProfileForm({ ...profileForm, address: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2">Pincode</label>
                                        <input
                                            className="w-full px-4 py-3 border-2 border-gray-100 rounded-xl focus:border-amber-500 outline-none transition-all"
                                            value={profileForm.pincode}
                                            onChange={e => setProfileForm({ ...profileForm, pincode: e.target.value })}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-gray-700 mb-2">New Password (Optional)</label>
                                        <input
                                            type="password"
                                            placeholder="Leave blank to keep current"
                                            className="w-full px-4 py-3 border-2 border-gray-100 rounded-xl focus:border-amber-500 outline-none transition-all"
                                            value={profileForm.password}
                                            onChange={e => setProfileForm({ ...profileForm, password: e.target.value })}
                                        />
                                    </div>
                                </div>

                                <div className="flex gap-4 pt-4">
                                    <button
                                        type="submit"
                                        disabled={profileLoading}
                                        className="flex-1 bg-amber-600 text-white py-4 rounded-xl font-bold text-lg shadow-lg hover:bg-amber-700 transition-all flex items-center justify-center gap-2"
                                    >
                                        {profileLoading ? <RefreshCw className="animate-spin" size={20} /> : <Check size={20} />}
                                        {profileLoading ? 'Updating...' : 'Save Changes'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setShowProfileModal(false)}
                                        className="px-8 py-4 border-2 border-gray-200 text-gray-500 rounded-xl font-bold hover:bg-gray-50 transition-all"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div >
    );
};

export default LandingPage;